import React, { useEffect, useMemo, useRef, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { X, DownloadIcon, CornerDownRight, MoreVertical } from "lucide-react";
import {
  BaseMessage,
  TextMessage
} from "@cometchat/chat-sdk-javascript";
import "../../styles/CometChatMessages/ChatHistoryModal.css";
import { useSearchParams } from "react-router-dom";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";

/* ===================== TYPES ===================== */
type ApiAttachment = {
  url: string;
  name: string;
  size: number;
  extension: string;
  mimeType: string;
};

type ReceiptUser = {
  uid: string;
  name: string;
  avatar?: string;
  deliveredAt?: number;
  readAt?: number;
};

type ActionEntity = {
  entity: {
    name: string;
    uid?: string;
    role?: string;
    status?: string;
    conversationId?: string;
  };
  entityType: "user" | "group";
};

type ApiMessage = {
  id: string;
  category: "message" | "action";
  type: "text" | "file" | "audio";
  sender: string;
  sentAt: number;
  deliveredAt?: number;
  readAt?: number;
  deletedAt?: number;
  receiverType?: "user" | "group";
  parentId?: string | undefined;
  replyCount?: number | undefined;
  data?: {
    text?: string;
    attachments?: ApiAttachment[];
    mentions?: Record<string, { name: string }>;
    receiverType?: "user" | "group"; // <- narrow type here
    entities?: {
      sender?: {
        entity?: {
          name: string;
          uid?: string;
        };
      };
      // ✅ new action entities (added, not removed)
      by?: ActionEntity;
      on?: ActionEntity;
      for?: ActionEntity;
    };

    // ✅ new action field
    action?: string;
    resource?: string;
  };
  receipts?: ReceiptUser[];
  messageInfo?: object;
};

/* ===================== HELPERS ===================== */
// function renderTextWithMentions(
//   text: string,
//   mentions?: Record<string, { name: string; uid?: string }>,
//   currentUserUid?: string,
//   receiverType?: "user" | "group"
// ) {
//   if (!text) return null;

//   // Split text into parts including mentions
//   const parts = text.split(/(<@.*?:.*?>)/g);

//   return parts.map((part, index) => {
//     const match = part.match(/<@(.*?):(.*?)>/);
//     if (!match) {
//       return <span key={index}>{part}</span>; // normal text
//     }

//     const type = match[1]; // "uid" or "all"
//     const id = match[2];   // actual UID or "all"

//     const classes = ["cometchat-mentions"];
//     let displayName = id;

//     if (type === "all") {
//       // Replace <@all:all> with @all
//       displayName = "all";
//       classes.push("cometchat-mentions-all", "cometchat-mentions-you");

//       // Determine outgoing vs incoming based on receiverType
//       if (receiverType === "user") {
//         // One-to-one chat → outgoing
//         classes.push("cometchat-mentions-outgoing");
//       } else if (receiverType === "group") {
//         // Group chat → incoming
//         classes.push("cometchat-mentions-incoming");
//       } else {
//         // default fallback
//         classes.push("cometchat-mentions-outgoing");
//       }
//     } else {
//       // Normal mention
//       displayName = mentions?.[id]?.name || id;

//       if (id === currentUserUid) {
//         classes.push("cometchat-mentions-you", "cometchat-mentions-incoming");
//       } else {
//         classes.push("cometchat-mentions-other", "cometchat-mentions-outgoing");
//       }
//     }

//     return (
//       <span key={index} className={classes.join(" ")}>
//         <span className="cometchat-text">@{displayName}</span>
//       </span>
//     );
//   });
// }

function renderTextWithMentions(
  text: string,
  mentions?: Record<string, { name: string; uid?: string }>,
  currentUserUid?: string,
  receiverType?: "user" | "group",
  messageType?: "text" | "info"
) {
  if (!text) return null;

  // Split text into parts including mentions
  const parts = text.split(/(<@.*?:.*?>)/g);

  return (
    <span className={messageType === "info" ? "cc-text-info" : undefined}>
      {parts.map((part, index) => {
        const match = part.match(/<@(.*?):(.*?)>/);
        if (!match) {
          return <span key={index}>{part}</span>; // normal text
        }

        const type = match[1]; // "uid" or "all"
        const id = match[2];   // actual UID or "all"

        const classes = ["cometchat-mentions"];
        let displayName = id;

        if (type === "all") {
          // Replace <@all:all> with @all
          displayName = "all";
          classes.push("cometchat-mentions-all", "cometchat-mentions-you");

          // Determine outgoing vs incoming based on receiverType
          if (receiverType === "user") {
            classes.push("cometchat-mentions-outgoing");
          } else if (receiverType === "group") {
            classes.push("cometchat-mentions-incoming");
          } else {
            classes.push("cometchat-mentions-outgoing");
          }
        } else {
          // Normal mention
          displayName = mentions?.[id]?.name || id;

          if (id === currentUserUid) {
            classes.push("cometchat-mentions-you", "cometchat-mentions-incoming");
          } else {
            classes.push("cometchat-mentions-other", "cometchat-mentions-outgoing");
          }
        }

        return (
          <span key={index} className={classes.join(" ")}>
            <span className="cometchat-text">@{displayName}</span>
          </span>
        );
      })}
    </span>
  );
}

/* ===================== CONTROLLER ===================== */
type ChatHistoryWithMessageInfoProps = {
  apiMessages: ApiMessage[];
  loggedInUid: string;
  onClose: () => void;
};

// export const ChatHistoryWithMessageInfo: React.FC<
//   ChatHistoryWithMessageInfoProps
// > = ({ apiMessages, loggedInUid, onClose }) => {
//   const [view, setView] = useState<"history" | "info" | "thread" | "">("history");
//   const [infoFrom, setInfoFrom] = useState<"history" | "thread">("history");
//   const [selectedMessage, setSelectedMessage] =
//     useState<ApiMessage | null>(null);
//   console.log("selectedMessage->", selectedMessage);
//   const [threadReplies, setThreadReplies] = useState<ApiMessage[]>([]);
//   useEffect(() => {
//     if (view !== "thread" || !selectedMessage) return;

//     const loadReplies = async () => {
//       try {
//         const messagesRequest = new CometChat.MessagesRequestBuilder()
//           .setParentMessageId(Number(selectedMessage.id))
//           .setLimit(20)
//           .build();

//         const replies = await messagesRequest.fetchPrevious();
//         setThreadReplies(replies as unknown as ApiMessage[]);
//       } catch (err) {
//         console.error("Thread fetch error:", err);
//       }
//     };

//     loadReplies();
//   }, [view, selectedMessage]);

//   return (
//     <>
//       {view === "history" && (
//         // <ChatHistoryModal
//         //   apiMessages={apiMessages}
//         //   loggedInUid={loggedInUid}
//         //   onClose={onClose}
//         //   onOpenMessageInfo={(msg) => {
//         //     setSelectedMessage(msg);
//         //     setView("info");
//         //   }}
//         // />
//         <ChatHistoryModal
//           apiMessages={apiMessages}
//           loggedInUid={loggedInUid}
//           onClose={onClose}
//           onOpenMessageInfo={(msg) => {
//             setSelectedMessage(msg);
//             setInfoFrom("history");
//             setView("info");
//           }}
//           onOpenThread={(msg) => {
//             setSelectedMessage(msg);
//             setView("thread");
//           }}
//         />
//       )}

//       {view === "info" && selectedMessage && (
//         <MessageInfoModal
//           loggedInUid={loggedInUid}
//           message={selectedMessage}
//           onClose={() => {
//             setView(infoFrom); // 👈 key line
//           }}
//         />
//       )}

//       {view === "thread" && selectedMessage && (
//         <ThreadModal
//           message={selectedMessage}
//           replies={threadReplies}
//           loggedInUid={loggedInUid}
//           onOpenMessageInfo={(msg) => {
//             setSelectedMessage(msg);
//             setInfoFrom("thread");
//             setView("info");
//           }}
//           onClose={() => {
//             setSelectedMessage(null);
//             setView("history");
//           }}
//           apiMessages={apiMessages}
//         />
//       )}
//     </>
//   );
// };

export const ChatHistoryWithMessageInfo: React.FC<
  ChatHistoryWithMessageInfoProps
> = ({ apiMessages, loggedInUid, onClose }) => {
  console.log("apiMessages->", apiMessages);
  const [view, setView] = useState<"history" | "info" | "thread">("history");

  // Thread state (parent message of thread)
  const [threadMessage, setThreadMessage] = useState<ApiMessage | null>(null);
  console.log("threadMessage->", threadMessage);
  const [threadReplies, setThreadReplies] = useState<ApiMessage[]>([]);

  // Info modal state (independent of thread)
  const [infoMessage, setInfoMessage] = useState<ApiMessage | null>(null);
  console.log("infoMessage->", infoMessage);
  const [infoFrom, setInfoFrom] = useState<"history" | "thread">("history");

  const [searchParams, setSearchParams] = useSearchParams();
  const [forceReloadThread, setForceReloadThread] = useState(false);

  const [threadLoading, setThreadLoading] = useState(false);

  // -----------------------------
  // Load thread replies (only once per thread)
  // -----------------------------
  useEffect(() => {
    if (view !== "thread" || !threadMessage) return;

    // skip only if replies exist AND not forced
    if (threadReplies.length > 0 && !forceReloadThread) return;

    const loadReplies = async () => {
      try {
        setThreadLoading(true);

        // const messagesRequest = new CometChat.MessagesRequestBuilder()
        //   .setParentMessageId(Number(threadMessage.id))
        //   .setLimit(20)
        //   .build();

        // const replies = await messagesRequest.fetchPrevious();
        // console.log("replies->", replies);
        // const threadReplies = replies.filter((r: any) =>
        //   apiMessages.some(
        //     (m: any) => Number(m.id) === Number(r.parentMessageId)
        //   )
        // );

        // console.log("Thread replies:", threadReplies);
        // setThreadReplies(replies as ApiMessage[]);
        // const threadReplies = replies
        // .filter((r: any) =>
        //   apiMessages.some(
        //     (m: any) => Number(m.id) === Number(r.parentMessageId)
        //   )
        // )
        // .map((r: any) => {
        //   const parentMsg = apiMessages.find(
        //     (m: any) => Number(m.id) === Number(r.parentMessageId)
        //   );

        //   return {
        //     ...r,
        //     messageInfo: parentMsg?.messageInfo ?? {}
        //   };
        // });

        const threadReplies = apiMessages
          .filter(
            (msg: any) =>
              Number(msg.parentId) === Number(threadMessage.id)
          )
          .map((msg: any) => ({
            ...msg,
            messageInfo: msg?.messageInfo ?? {}
          }));

        console.log("Thread replies:", threadReplies);

        setThreadReplies(threadReplies);
      } catch (err) {
        console.error("Thread fetch error:", err);
      } finally {
        setThreadLoading(false);
      }
    };

    loadReplies();
  }, [view, threadMessage, forceReloadThread]);


  // -----------------------------
  // Restore thread from URL (safe restore)
  // -----------------------------
  useEffect(() => {
    const threadId = searchParams.get("threadId");

    if (!threadId || !apiMessages?.length) return;

    // Prevent overriding if thread already open
    if (threadMessage) return;

    const found = apiMessages.find(m => String(m.id) === threadId);
    if (found) {
      setThreadMessage(found);
      setView("thread");
    }
  }, [apiMessages, searchParams]);

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <>
      {/* HISTORY */}
      {view === "history" && (
        <ChatHistoryModal
          apiMessages={apiMessages}
          loggedInUid={loggedInUid}
          onClose={onClose}
          onOpenMessageInfo={(msg) => {
            setInfoMessage(msg);
            setInfoFrom("history");
            setView("info");
          }}
          onOpenThread={(msg) => {
            setThreadMessage(msg);
            setThreadReplies([]); // reset for new thread
            setView("thread");

            // store thread id in URL
            setSearchParams({ threadId: String(msg.id) });
          }}
        />
      )}

      {/* INFO MODAL */}
      {view === "info" && infoMessage && (
        <MessageInfoModal
          loggedInUid={loggedInUid}
          message={infoMessage}
          onClose={() => {
            if (infoFrom === "thread") {
              setThreadReplies([]);          // clear old replies
              setForceReloadThread(true);    // trigger reload
            }

            setView(infoFrom);
          }}
        />
      )}

      {/* THREAD */}
      {view === "thread" && threadMessage && (
        <ThreadModal
          message={threadMessage}
          replies={threadReplies}
          loading={threadLoading}
          loggedInUid={loggedInUid}
          apiMessages={apiMessages}
          onOpenMessageInfo={(msg) => {
            setInfoMessage(msg);
            setInfoFrom("thread");
            setView("info");
            setForceReloadThread(true); // ensure fresh load
          }}
          onClose={() => {
            setThreadMessage(null);
            setThreadReplies([]);
            setView("history");

            // remove thread id from URL
            setSearchParams({});
          }}
        />
      )}
    </>
  );
};

export const formatSize = (bytes: any) => {
  if (bytes < 1024) return `${bytes} bytes`;

  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;

  const mb = kb / 1024;
  if (mb < 1024) return `${Math.round(mb)} MB`;

  const gb = mb / 1024;
  return `${Math.round(gb)} GB`;
};

export const downloadFile = async (url: string, fileName?: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = blobUrl;
    link.download = fileName || "download";

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("File download failed", error);
  }
};

export const isEmojiOnly = (text: string) =>
  /^\p{Extended_Pictographic}+$/u.test(text);

/* ===================== CHAT HISTORY MODAL ===================== */

type ChatHistoryModalProps = {
  apiMessages: ApiMessage[];
  loggedInUid: string;
  onClose: () => void;
  onOpenMessageInfo: (msg: ApiMessage) => void;
  onOpenThread: (msg: ApiMessage) => void;   // 👈 add this
};

const ChatHistoryModal: React.FC<ChatHistoryModalProps> = ({
  apiMessages,
  loggedInUid,
  onClose,
  onOpenMessageInfo,
  onOpenThread
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [membersCount, setMembersCount] = useState<string | number>(0);
  const [avatar, setAvatar] = useState<string | null>(null);

  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

  const messages = useMemo(() => {
    return [...apiMessages]
      // .filter((m) => m.category === "message") // include deleted messages
      .sort((a, b) => a.sentAt - b.sentAt);
  }, [apiMessages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  function getReceiptClass(msg: ApiMessage) {
    if (msg.readAt) return "cometchat-message-bubble__status-info-view-receipts-read";
    if (msg.deliveredAt) return "cometchat-message-bubble__status-info-view-receipts-delivered";
    return "cometchat-message-bubble__status-info-view-receipts-sent";
  }

  const formatChatDate = (unixSeconds: number) => {
    const msgDate = new Date(unixSeconds * 1000);
    const today = new Date();

    const isToday =
      msgDate.getDate() === today.getDate() &&
      msgDate.getMonth() === today.getMonth() &&
      msgDate.getFullYear() === today.getFullYear();

    if (isToday) return "Today";

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isYesterday =
      msgDate.getDate() === yesterday.getDate() &&
      msgDate.getMonth() === yesterday.getMonth() &&
      msgDate.getFullYear() === yesterday.getFullYear();

    if (isYesterday) return "Yesterday";

    // const day = msgDate.getDate();
    const day = String(msgDate.getDate()).padStart(2, "0");
    const month = msgDate.toLocaleString("en-US", { month: "short" });
    const year = msgDate.getFullYear();

    return `${day} ${month}, ${year}`;
  };

  const ChatHistorySkeleton = () => {
    // We'll alternate in/out for visual effect
    const rows = Array.from({ length: 8 });

    return (
      <>
        {rows.map((_, i) => {
          const isIncoming = i % 2 === 0; // alternate between in/out
          return (
            <div key={i} className={`cc-row ${isIncoming ? "in" : "out"}`}>
              <div className="cc-avatar-wrapper">
                <div className="skeleton-avatar shimmer" />
              </div>

              <div className={`cc-bubble ${isIncoming ? "in" : "out"}`}>
                <div className="skeleton-line short shimmer" />
                <div className="skeleton-line long shimmer" />
              </div>
            </div>
          );
        })}
      </>
    );
  };

  // Show/hide down arrow based on scroll
  const handleScroll = () => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    // show button ONLY if user scrolled up
    setShowScrollDown(distanceFromBottom > 40);
  };
  // Scroll to bottom
  const scrollToBottom = () => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (!scrollRef.current) return;

    if (!showScrollDown) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages]);

  // useEffect(() => {
  //   const pathParts = window.location.pathname.split('/');
  //   const type = pathParts[pathParts.length - 2]; // 'user' or 'group'
  //   const uid = pathParts[pathParts.length - 1];   // the actual ID

  //   if (uid) {
  //     if (type === "group") {
  //       CometChat.getGroup(uid).then(
  //         group => {
  //           setGroupName(group.getName());
  //           setMembersCount(`${group.getMembersCount()} Members`);
  //         },
  //         error => console.log("Group fetch failed", error)
  //       );
  //     } else {
  //       CometChat.getUser(uid).then(
  //         user => {
  //           setGroupName(user.getName());
  //           // For users, show status (Online/Offline) instead of member count
  //           // Capitalize the first letter (e.g., "online" -> "Online")
  //           const status = user.getStatus();
  //           const formattedStatus = status.charAt(0).toUpperCase() + status.slice(1);

  //           setMembersCount(formattedStatus);
  //         },
  //         error => console.log("User fetch failed", error)
  //       );
  //     }
  //   }
  // }, []);

  const getRoleLabel = (role: string | undefined) => {
    switch (role) {
      case "manager":
        return "RA";
      case "lc":
        return "LC";
      default:
        return "Client";
    }
  };

  useEffect(() => {
    const loggedInUser = CometChatUIKitLoginListener.getLoggedInUser();
    const pathParts = window.location.pathname.split('/');
    const type = pathParts[pathParts.length - 2]; // 'user' or 'group'
    const uid = pathParts[pathParts.length - 1];

    if (!uid) return;

    if (type === "group") {
      CometChat.getGroup(uid).then(
        group => {
          setGroupName(group.getName());
          setMembersCount(`${group.getMembersCount()} Members`);
          setAvatar(group.getIcon() || null); // 👈 group avatar
        },
        error => console.log("Group fetch failed", error)
      );
    } else {
      CometChat.getUser(uid).then(
        user => {
          // setGroupName(user.getName());
          const role = user.getRole() || "";
          const name = user.getName() || "";

          if (loggedInUser && ["manager", "lc"].includes(loggedInUser.getRole())) {
            const fullNameRole = `${name} (${getRoleLabel(role)})`;
            console.log("fullNameRole:", fullNameRole);
            setGroupName(fullNameRole);
          } else {
            console.log("Setting group name to user name:", name);
            setGroupName(user.getName());
          }
          const status = user.getStatus();
          setMembersCount(status.charAt(0).toUpperCase() + status.slice(1));
          setAvatar(user.getAvatar() || null); // 👈 user avatar
        },
        error => console.log("User fetch failed", error)
      );
    }
  }, []);

  return (
    <>
      <div className="history-modal-overlay">
        <div className="history-modal">
          <div className="history-header">
            {/* <h3>Chat History – {groupName}</h3> */}
            {/* 1. Leading View (Avatar) */}
            <div className="cometchat-list-item__leading-view">
              {avatar ? (
                <img
                  src={avatar}
                  alt={groupName}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    objectFit: "cover"
                  }}
                />
              ) : (
                <div
                  className="cometchat"
                  style={{
                    height: "24px",
                    width: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                >
                  <div
                    className="cometchat-avatar"
                    style={{ width: "40px", height: "40px" }}
                  >
                    <span className="cometchat-avatar__text">
                      <ChatHistoryAvatar name={groupName} size={40} />
                    </span>
                  </div>
                </div>
              )}
              {/* <div className="cometchat" style={{ height: '24px', width: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div className="cometchat-avatar" style={{ width: "40px", height: "40px" }}>
                  <span className="cometchat-avatar__text">
                    <ChatHistoryAvatar name={groupName} size={40} />
                  </span>
                </div>
              </div> */}
              <div className="cometchat-status-indicator cometchat-list-item__status">
                <div className="cometchat-list-item__status-icon"></div>
              </div>
            </div>
            {/* 2. Body (Title & Members) - Now correctly nested inside the header flexbox */}
            <div className="cometchat-list-item__body" style={{ flexGrow: 1, marginLeft: '12px' }}>
              <div className="cometchat-list-item__title-container">
                <div className="cometchat-list-item__body-title_test">
                  {groupName || "Novel Test Matter"}
                </div>
                <div className="cometchat-list-item__body-subtitle">
                  <div className="cometchat-message-header__subtitle" style={{ color: "#A1A1A1", font: "var(--cometchat-font-caption1-regular)" }}>
                    {membersCount || 0}
                  </div>
                </div>
              </div>
            </div>
            <button className="chathistory-close" onClick={onClose}><X size={20} /></button>
          </div>
          <div className="history-body" ref={scrollRef}
            onScroll={handleScroll}
            style={{
              position: "relative",   // ✅ anchor for absolute button
              overflowY: "auto",
              height: "70vh",
            }}>
            {messages.length === 0 ? (
              <ChatHistorySkeleton />
            ) : (
              messages.map((msg, index) => {
                const isOutgoing = msg.sender === loggedInUid;
                const date = formatChatDate(msg.sentAt);
                const prevDate = index > 0 ? formatChatDate(messages[index - 1].sentAt) : null;

                return (
                  <React.Fragment key={msg.id}>
                    {msg.parentId === undefined && date !== prevDate && <div className="cc-date-separator">{date}</div>}
                    {msg.category === "action" &&
                      msg.data?.entities?.by &&
                      msg.data?.entities?.on && msg.data.action === "added" && (
                        <div className="cc-group-info-separator">
                          {msg.data.entities.by.entity.name}{" "}
                          {msg.data.action}{" "}
                          {msg.data.entities.on.entity.name}
                        </div>
                      )}
                    {msg.category === "action" &&
                      msg.data?.entities?.by && msg.data.action === "joined" && (
                        <div className="cc-group-info-separator">
                          {msg.data.entities.by.entity.name}{" "}
                          {msg.data.action}
                        </div>
                      )}
                    {msg.category === "action" &&
                      msg.data?.entities?.by && msg.data.action === "left" && (
                        <div className="cc-group-info-separator">
                          {msg.data.entities.by.entity.name}{" "}
                          {msg.data.action}
                        </div>
                      )}
                    {msg.category === "action" &&
                      msg.data?.entities?.by && msg.data.action === "kicked" && (
                        <div className="cc-group-info-separator">
                          {msg.data.entities.by.entity.name}{" "}
                          {msg.data.action}{" "}
                          {msg?.data?.entities?.on?.entity?.name}
                        </div>
                      )}
                    {msg.category === "message" &&
                      <div
                        className={`cc-row ${isOutgoing ? "out" : "in"} ${highlightedMessageId === msg.id ? "out-hightlight-message" : ""
                          } group relative`} // 'group' tracks hover for this specific div only
                        // Use an arrow function so it only runs when the event happens
                        onMouseEnter={() => { isOutgoing && setHighlightedMessageId(msg.id); }}
                        // Optional: Clear the highlight when the mouse leaves
                        onMouseLeave={() => { isOutgoing && setHighlightedMessageId(null); }}
                      >
                        {/* This button stays hidden (opacity-0) UNLESS the parent 'group' is hovered */}
                        {highlightedMessageId === msg.id &&
                          <button
                            className="menu-action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setHoveredMessageId(msg.id);
                            }}
                          >
                            <MoreVertical size={16} color="#A1A1A1" />
                          </button>
                        }
                        {msg.parentId === undefined && msg?.receiverType === "group" && !isOutgoing && (
                          <div className="cc-avatar-wrapper">
                            <Avatar name={msg?.data?.entities?.sender?.entity?.name} />
                          </div>
                        )}
                        {msg?.receiverType === "group" && !isOutgoing
                          ? (
                            msg.parentId === undefined && <div className="cc-message">
                              <div className="cc-sender-name">{msg?.data?.entities?.sender?.entity?.name}</div>
                              <div
                                className={`cc-bubble ${isOutgoing ? "out" : "in"}`}
                              >
                                {msg.deletedAt ? (
                                  <div className="cometchat-message-bubble__body-content-view">
                                    <div className="cometchat">
                                      <div
                                        className={`cometchat-delete-bubble ${isOutgoing
                                          ? "cometchat-delete-bubble-outgoing"
                                          : ""
                                          }`}
                                      >
                                        <div className="cometchat-delete-bubble__body">
                                          <div className="cometchat-delete-bubble__icon"></div>
                                          <label className="cometchat-delete-bubble__text">
                                            This message was deleted
                                          </label>
                                        </div>
                                        <div className="cc-time">
                                          {new Date(msg.sentAt * 1000).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {/* {msg.type === "text" && (
                                        <div className="cc-text">
                                          {renderTextWithMentions(
                                            msg.data?.text || "",
                                            msg.data?.mentions,
                                            loggedInUid,
                                            msg.data?.receiverType
                                          )}
                                        </div>
                                      )} */}

                                    {msg.type === "text" && (
                                      <div className="cc-text">
                                        {renderTextWithMentions(
                                          msg.data?.text || "",
                                          msg.data?.mentions,
                                          loggedInUid,
                                          msg?.receiverType,
                                          "text"
                                        )}
                                      </div>
                                    )}

                                    {/* AUDIO */}
                                    {msg.type === "audio" && (
                                      <AudioMessage
                                        msg={msg}
                                        isOutgoing={isOutgoing}
                                        downloadFile={downloadFile}
                                      />
                                    )}

                                    {msg.type === "file" &&
                                      msg.data?.attachments?.map((file) => {
                                        const ext = file.extension.toLowerCase();
                                        const isImage = ["png", "jpg", "jpeg", "webp"].includes(ext);
                                        const isPDF = ext === "pdf";
                                        const isText = ext === "txt";
                                        const isXLSX = ext === "xlsx";
                                        const isDOCX = ext === "docx";
                                        const isPPTX = ext === "pptx";
                                        const isDocument = [
                                          "doc",
                                          "xls",
                                          "ppt"
                                        ].includes(ext);

                                        const videoFormats = ["mp4", "mov", "avi", "mkv", "webm", "flv", "wmv", "m4v"];

                                        const audioFormats = ["mp3", "wav", "ogg", "flac", "aac", "m4a"];

                                        if (isImage) {
                                          return (
                                            <div key={file.url} className="cc-image">
                                              <img
                                                src={file.url}
                                                alt={file.name}
                                                onClick={() =>
                                                  setPreviewImage(file.url ?? null)
                                                }
                                              />
                                            </div>
                                          );
                                        } else if (isPDF) {
                                          return (
                                            <div
                                              key={file.url}
                                              className="cc-file"
                                            >
                                              <img
                                                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI/SURBVHgB1Ve/L2xBFP7m7L2bt7F5b98riO4hCgmFSkWEmmi1Qq3X06j8FXQWtR8lUWyIRiIhGiHBJn6sFXfGOWsb6y5zZ+9d8SWTmcyczPedM2fmngswjDH/uW1zuzXNwapwCreqDgrccmguitz6RcAqDyZqV83lJcz5OaKCenqAbNbWfEcEmA/k+/t4WVwEymVEBc3MIDU6CmQyNuZFCpsN8nkn8gqenqDZAZRKNtY5QhJgclsRyQiIICI5AZYiYhdgDg8RRYSHuAUcHOD6+Bjl9naklKp4KP2vkxNkp6aSFyDI8U24YRF3QQCPyT0ikO+H2jofgWppgRoYqLv+L53GXyYVchEhUQiDcwRofh7U2opgeRl6YyPU5rfnwdcaz9xiFyAR0Ht7APefIVONgKojwPkI9O4uSI7g6upLW1/yIG4Bpuq9OT21sld15t3fgYeHtw16e9EInAXQ2FjFe5qchOrogCvck7CvD8HCAtTICFJzczCcE1IHKL4ZenMTemvLah+nCBCTyhEoDr9qa4PhRCSuASQppZBJzc5a7xUpAnL1aHwcSsJ/dFQ5AsOeGhaj2XN5mFRnJ4KlJes9rQWIdzQ9/Xb/19cRrKy8W5comDoP0qf7WhtyWIVE7n0teSOwjoDmJ1cS7CVG8mgCHMJrg2Qrop8r4IsvXNwCirWT90NDKHE1Exdkr9vBwdA1+TPa5n64duHx4gLFQgFpavyU/K4u/OnuDlta+/afU+JK5UwG3PJoDoR4RziF+xXwDkgcKZ6uuwAAAABJRU5ErkJggg=="
                                                alt="PDF"
                                                // style={{ width: "24px", height: "24px" }}
                                                className="cometchat-file-bubble__leading-view"
                                              />
                                              <div className="cc-file-info">
                                                <div
                                                  className="cc-file-name"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                                <div className="cc-file-meta">
                                                  {Math.round(file.size / 1024)} KB •{" "}
                                                  {file.extension.toLowerCase()}
                                                </div>
                                              </div>
                                              <DownloadIcon
                                                size={20}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.stopPropagation();      // ✅ prevent window.open
                                                  e.preventDefault();
                                                  downloadFile(file.url, file.name);
                                                }}
                                                color={!isOutgoing ? "#e09029" : "#ffffff"}
                                              />
                                            </div>
                                          );
                                        } else if (isText) {
                                          return (
                                            <div
                                              key={file.url}
                                              className="cc-file"
                                            >
                                              <img
                                                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAH5SURBVHgB3ZfLSxxBEMa/7s48FkIiG3LYJWxiSMwp5HHJNZBAcgrJHxrMHuJVbyJ7EEEEX+wDcdVdcPGgzmvHqlEPrqvMTPes4g+K7uluur6pLmp6ACKO41dk82SH8WSYZZ/sW1x0lsmmMFkGZJ9YwCx1fl+bHRzjoH+ErNRePIPrWmmXL7CAeHR0c3sPf+tLCMIIWfnx7QM+vn8J236UZvlAjhtdbGzkcs74foiNrW7SpmBKogCyiChEQBYRhQlIK8K4gFb7AFlEpErVLDQ7PbQ6u3j6xIEUEkLKpN3Z7eHn98/FC2BiWOjuH8H3TiClglQKyhrvqhABTKn0mN5cIQy9JAJSCGgLcB0LMzOVW9esb3bhnQZJ33FLkL7EcBhBCKkvgKlVy8mGzOvpCriMNptd+qoI/qphba1zZb1lO4iikKYNRODUC1Cfa2AYDZPnP7++0OYR6v8bEDh3kJy3urotP0tlKAeUssjO+5xgLMCy7BtDfMkNATBVBwTyYkRAfveGBMTIj6EjyC/hYeSATgS0SnGr3UvqvQ5aAlZW2wgCj+5/LvKidQRcXnWcawswwf0U4Do2JilgMDr47s1zSi4fpuC93k6Xx87xn9E8tV9HJ/r9Q7pcdKCkgi7VShm1WnXcVP3Of07pqiZa3CH7h8nAjhfYJ/s+A9xpLS5WrHVdAAAAAElFTkSuQmCC"
                                                alt="TXT"
                                                // style={{ width: "24px", height: "24px" }}
                                                className="cometchat-file-bubble__leading-view"
                                              />
                                              <div className="cc-file-info">
                                                <div
                                                  className="cc-file-name"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                                <div className="cc-file-meta">
                                                  {formatSize(file.size)} • {file.extension.toLowerCase()}
                                                </div>
                                              </div>
                                              <DownloadIcon
                                                size={20}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.stopPropagation();      // ✅ prevent window.open
                                                  e.preventDefault();
                                                  downloadFile(file.url, file.name);
                                                }}
                                                color={!isOutgoing ? "#e09029" : "#ffffff"}
                                              />
                                            </div>
                                          );
                                        } else if (isXLSX) {
                                          return (
                                            <div
                                              key={file.url}
                                              className="cc-file"
                                            >
                                              <img
                                                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMRSURBVHgBxVddSBRRFP5m9tfVQhLZqEzTJMkQwwQRIQt6kCiEtF4qeqkgo6B8KurBZ+mHIkoyzIcgsUzChDDcIMjC0MQty8RFDf9zU2TX/e3cu9vm/tU4u04fXGY4d+aeb875zrl3AILX682g0UljzqsMWphP5lvw3/TQSIaysNLYyQi00E156OzUzDxGx39EfTs1ZS2/Ts/OB9lzstYj0aCHRJgYAW+o9UO/BTfrX8LhcEV981bNUU7iyJnbEAQxYD9RWYLSohzo9VpIgFWMZG3r6P2rcwbL2Az6B0aQk2nE9uwNAbttyYlu+gC73QEJSFZDJmrvvYDb5UBz3QWehrNXGgORsNt9JHbtyPhnJGQT2L83HwaDDqa3n7CwsAivxw1B9SegUknIJlC2J49r4NDJaxDJsSiELyWFhGwCdx52wOm0oaa6Eja7C7V17dxuHviOooIsySRkE/g8NMGvudvSqGR/wuNxQRTV6P86hsHhERjXJUEQBXpC4NoYtozjWMXu+BG4er6cUrAGVZcewEVixLJiXnKK+DYyDZdziTsXRRU0msiuZBNITUniBCanrVBr9aSD4KW0ukTShQput5MiIQb1irgQqLrcwL/86f1qKsMFfxkKwYsTMcElUoV4wuZiJpCZboQhQQcz5Xxyas5fhuHLqdRa0oeb0hDnCFw8VcbLsOL0dbadUgo0UZ9lGhBoxJVA26te6HUicrM3kuh8rVkOZBNoN/Xx3DbdPQfzl1HU3GjlYlOMAFM1a71Nz7swMTVLWSANQEECv/GkvdsnwCg5XjUCxytKkJjga62LNgcam99ADmQTKMzLCJyKWCtueGwKa0arSoCh/lEHa/U4sK8QQb14BVi5apahu28YPeYxxALZEXjdNYDDB4uRZEjAu55B8FAoSaC57b1vFwRvhFCrJR1C40eA6g5qjeTjd1TEpIF4ICIBtsspScAaaiwuSOdH7niBrVWUnxZxjv0ZddK1NHRiYnIWH81DfCuNFVs2G7E1c1Okqdb//nMq0lHJwm5oPIMyYI5NzCfz/QsYRIzAPAyESAAAAABJRU5ErkJggg=="
                                                alt="Xlsx"
                                                // style={{ width: "24px", height: "24px" }}
                                                className="cometchat-file-bubble__leading-view"
                                              />
                                              <div className="cc-file-info">
                                                <div
                                                  className="cc-file-name"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                                <div className="cc-file-meta">
                                                  {Math.round(file.size / 1024)} KB •{" "}
                                                  {file.extension.toLowerCase()}
                                                </div>
                                              </div>
                                              <DownloadIcon
                                                size={20}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.stopPropagation();      // ✅ prevent window.open
                                                  e.preventDefault();
                                                  downloadFile(file.url, file.name);
                                                }}
                                                color={!isOutgoing ? "#e09029" : "#ffffff"}
                                              />
                                            </div>
                                          );
                                        } else if (isDOCX) {
                                          return (
                                            <div
                                              key={file.url}
                                              className="cc-file"
                                            >
                                              <img
                                                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAK7SURBVHgBzZdPTxNBGMafmd0thQoWo0SJiQjqAQSjJkSjiZoQTgb5BHoyxC/B1ZN+C08kolcvEKMSPYgtMRCkFQ0BMREahZb+m/F9t00jbUlmsC08yWQ3M7Pz/OZ98+7OAiStdRe1KWqbujGaZE/2FsWbWWphNFYJapcZYJJuRstHf2xofFvXsFXvGYnWFuPp0wxQ4fJ+XuHxsxzSWVjr0T2JoasuWpqMpidktd7nr9W+zFnJHd5AHsm00fSwRB2USptD1AXABqJuAKYQNQf4FNNWEC5qrMiSxuLXX+hsJ0fhQAhJzcHS92Y8HDlSfwBWKh/GwsoG8pk/ENL1m+cKGmkQAMsLHvONVS5VgqimugH4iwfaoKQHpbJ+Gv4LIBQsXLd37Pqk2wyhyEaKqusaV8HoTQdjd3cvMv7Axfh92plWpb6xERdDV9SueYKisFcKjAHm4hrXLzrklSn1dZ8COtpB7/1cqW+gW+Djwm+6K4PYw8oYIBpXlEeBjrAuGklEvqQRXUzj7EldBBJ0tsgjvqp9SxNZvYhmPlMUevkRuvYJvI2mMLcscaOfw6vQ0yl8KIfyXhcAjsKlHtcH4FBHY3nMLAj093BqNK71SR9KOgHjNS0joDBw3sOJthyUzmF9U2A7JegA4iHUlCU4hqLcC7PdWwNspYD4WqEiYis5KrFACWxsJICllSySGQ+m4bcGYHE1DA824d0/oWaA4cEg3kSS1OdZrWf9JmSz461ZRGJceoWdxtY0Xn3Ywcxcmuo9aLWeNUCMSuzpRJ7SHCKzAsA2pebJBJ3hdJAiYBfUfX0LpFN54pTS7BRa8RwOWIcTINSMhgIkyjvv9G/RQSKJWonXutW3WXWM/4ym6Hq7fGD1ZxKz8wk61tnVdTWdOx3Aha6j1YZeHvjPqaRP7DLfUHuBxoiNp9mTvf8CwVZ4KCLQmwsAAAAASUVORK5CYII="
                                                alt="DOCX"
                                                className="cometchat-file-bubble__leading-view"
                                              />
                                              <div className="cc-file-info">
                                                <div
                                                  className="cc-file-name"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                                <div className="cc-file-meta">
                                                  {Math.round(file.size / 1024)} KB •{" "}
                                                  {file.extension.toLowerCase()}
                                                </div>
                                              </div>
                                              <DownloadIcon
                                                size={20}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.stopPropagation();      // ✅ prevent window.open
                                                  e.preventDefault();
                                                  downloadFile(file.url, file.name);
                                                }}
                                                color={!isOutgoing ? "#e09029" : "#ffffff"}
                                              />
                                            </div>
                                          );
                                        } else if (isPPTX) {
                                          return (
                                            <div
                                              key={file.url}
                                              className="cc-file"
                                            >
                                              <img
                                                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAJHSURBVHgB1ZdNaxNBGMf/M7uTxHRTqx5K0EIR1HoSv4Dk4KWI4DfwI3jy4En0s4j0Vl8uHtQUvVVMD0HFolLEBg+NTRNtsuvujs/TFLTZFLOzL+I/DNl52fn/ZubZ2VmApLWep1SntK3z0TJ7srfYv1ijNIN81aF0kQGW6eJapLq7CbQ/IbaqF4DS9KStVxhAR4o3XgJPbgH+ALF16SawcBUolCdp3ZFji9fumZmzvF0awIvh/981I5GFYkBkAxADIjuACSHSB/jyCnEgbKQOsIrtz2/hOnOQUsASNEohUGy9R7l2IwcA0jH5Hd+2mugOQtgEoYhCKjW2bSYArONlm8xD9H8SBAFYBJIeAG80f8rr0c7ZArbWDxRPlyQURZkXgpYiTYDLt+Gu1xG6vb2sLFZgz56D1SaAZ3eA/XLWkYKEHehDw914CdzGfVit1xD08ynvqSlUri/BP3sFdnPpQFuOAZ3qEvCNNKU8ut/d0mPWasC1HEge8IjfISuQdB8Y6bVSRdDvIY6MZ0CdqUGcODnMFB3gdA3BVBX+h3qEKxOAwCEz5QwzFHTBu+foNx+jIn5A2pMTGAN4FITqa4OOVMM8P24lC3sbTxwZA5AXSrZAPLuojINQCIHk9oYA/ptHCAZdaGgkldES6Kd3UQg1hEo+A0YAigPASm7OyvZE9P8C0NstT4DOaOHO/CJ2+SWekriv9tzi2Dr+MqLNG7XITe1N7HxcRcFCYtmzCzh66vy4qof//OOUDqxigy8oPUA+YuMV9mTvX6exKJpgmkTmAAAAAElFTkSuQmCC"
                                                alt="PPTX"
                                                className="cometchat-file-bubble__leading-view"
                                              />
                                              <div className="cc-file-info">
                                                <div
                                                  className="cc-file-name"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                                <div className="cc-file-meta">
                                                  {formatSize(file.size)} • {file.extension.toLowerCase()}
                                                </div>
                                              </div>
                                              <DownloadIcon
                                                size={20}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.stopPropagation();      // ✅ prevent window.open
                                                  e.preventDefault();
                                                  downloadFile(file.url, file.name);
                                                }}
                                                color={!isOutgoing ? "#e09029" : "#ffffff"}
                                              />
                                            </div>
                                          );
                                        } else if (isDocument) {
                                          return (
                                            <div
                                              key={file.url}
                                              className="cc-file"
                                            >
                                              <div className="cc-file-thumb">📄</div>
                                              <div className="cc-file-info">
                                                <div
                                                  className="cc-file-name"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                                <div
                                                  className="cc-file-meta"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >
                                                  {Math.round(file.size / 1024)} KB •{" "}
                                                  {file.extension.toLowerCase()}
                                                </div>
                                              </div>
                                              <DownloadIcon
                                                size={20}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.stopPropagation();      // ✅ prevent window.open
                                                  e.preventDefault();
                                                  downloadFile(file.url, file.name);
                                                }}
                                                color={!isOutgoing ? "#e09029" : "#ffffff"}
                                              />
                                            </div>
                                          );
                                        } else if (videoFormats.includes(ext)) {
                                            return (
                                              <div
                                              key={file.url}
                                              className="cc-file"
                                            >
                                              <img
                                                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI/SURBVHgB1ZfPaxNBFMe/O/tjdtc2JoKgBSXoqXrSk0ftxUNB1KP4H/iHqada0WtSClIVtDdpoYTQQtWqSbYlyf5Isr7ZiIcwxtnsbkq/8Nhlfuz77Hszb2cBUhzHVbI6WTuej9aET+Fb+3OzTVbGfNUhuyUA1ujm4WRv2xvh6NcQaXV1SYdjM9XhGwIgnmzdbUR4+baLaBAjrVbvubh9w4JlaSrDO1LUd5/8mZwLBWGMHXqBMFSaX1aOVVEQhQCkgSgMQBUid4DGfoQ0EAbyBjgYoLnfQnkxgqbp0BgDo+vBoYvVlQvFAwiNtBIOj9oIgmMwZiRmGPJtmToFly/qKJf+P812K7CdyhhANygS8ndNHYFH988lELX3Pupb/aljub0InZwPhxGlgeUD4PBxKFfu2EnFqxHE9pfwn+MN00mioDFd2p9pF4hUPKaIPHmwMDUtTDcTCCkgctDydZPsfJKWrc8+/ECy5bScFuE0ibQ8e1pK1oiqci9E3AJc3oPkIytVrnVgr9nDi9ffcNIzYAmSeQG0OhGer3+lKhgmW8/iJrUqnQeyAfT9ETY/tLD50cModuC4C7TW1BxnBhiH+zuOuwymWaJSO9ujUs96UzuB53l/w81t9XDnArDbGFJp5XAp3NBmdzwzgHCqG2orXEWFnojOLgDn2XObBqAz2XjzWoBB1EdeiuhZy9WutE/8GdXpeney48fPLnb22smBIquWLnFUr1RkXeun/nPKqHQ2xQ3ZK8xHwvGG8Cl8/waJH0DPGwrofQAAAABJRU5ErkJggg=="
                                                alt="VIDEO"
                                                className="cometchat-file-bubble__leading-view"
                                              />
                                              <div className="cc-file-info">
                                                <div
                                                  className="cc-file-name"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                                <div className="cc-file-meta">
                                                  {formatSize(file.size)} • {file.extension.toLowerCase()}
                                                </div>
                                              </div>
                                              <DownloadIcon
                                                size={20}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.stopPropagation();      // ✅ prevent window.open
                                                  e.preventDefault();
                                                  downloadFile(file.url, file.name);
                                                }}
                                                color={!isOutgoing ? "#e09029" : "#ffffff"}
                                              />
                                            </div>
                                            );
                                        } else if (audioFormats.includes(ext)) {
                                          // Choose icon based on audio type
                                          let audioIcon = "";

                                          if (ext === "ogg") {
                                            // Custom icon for OGG files
                                            audioIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAH6SURBVHgBxVfbasJAEJ3EeL8QURG8QB/EF5/6B/2E/mnbL9B3hf6B9s07jTdUUNM9SxMkTSCb3dgDQ5bdDedkZnZmQ8Rg2/YTsz6zb/sxeAMnuLXfwSczkx4Li9kzBLyxwat39Xw+0/F4JFEUi0VKJBJhtw8gwPbOWpZFk8mEbrcbiaLdblO1WiVd18Nst3x3LRaLSOTA9XrlHxDyfTOUzDhFxCJARERsAsKKUC5gt9uRiAiDYhAwn8/5Kbi3crlMnU4nfgFALpej7XZLh8OB1wTH/BCLAKBUKnFSFDQ8g+pCZAGmaXID4Pb1ev1nTz6f5+SXyyXQA5GSsNVqUbPZpOVyya1er1O32yWfokqZTIabshAgviAcDod0Op0omUzyTO/1enzNr38YhqFOAL5yOp3yBEPjATRNc9eC4OyRFoAv3Gw2lM1m3blGo8Hn0UMgKohMiQAglUq5Y+RDOp2m0WjEQyBCDkhVwkql4uYD4gwThZSAQqHA3Q7ce0UEUoVotVrRfr/nIYgKKQ8g3vBCFNc7kPIAcgA1AJUw5BVMrYDxeMxDAC9EhZQAVDc0HdGjdw/pC4kMuRIBsvAVIPBjoUSA5Z1E+8RFQhXQNYOOKv6M+uz54l3AlWo2m0mdcQdoULVazW/p499/TnWWxV8YMHunxwDEA3CC+wfREERCXNxfDQAAAABJRU5ErkJggg==";
                                          } else {
                                            // Default audio icon for other formats
                                            audioIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI4SURBVHgBxVfPaxNBFP5msttkA8GkuYlo7MGbsj0JXvTsxSgeBIV48ij1L2h79CKIRw/BP0BTUeypZm96M6g9qKBREESsDUTT/NhkfLO20GQ3MNOdTT947GTeZL9v33vzdhYgCCFKZHWybTEb1CSn5Ga7gzdkecwWLbJFKaBGg/Kkd/TrC0Y/NqELvnAOPDuvutyTAsTkrP/+GTrVa0C/A11krj6AfbYClsmpLG/xqNn+y3sHIpcQvTY9wHOIbltleZ4jAeiISESAjojEBKiKMC5g+GEDOiIsGIb/cQNbnxsYFE+BM4YUQ2DOtwYKV+6aF2CfLmPwbm1s7oi/he2vr9AeEAFnZBRqay7y/7FSYJ0pw7lVC66TKGTI0gjILS7ApzDFEsCdPEa/m3BuVKn53Az5c/TQOVvAohRIMy5AYvD6EToPLyN9cRnZ23Wkjrljfsf6b4lEYA/DTx7+LJ9Ef30VmevVUEpsYrGTisB++CSk++QOCbgU8k3hN78NZTSYo/5mT6QT+m/XlNcm2opVoJUCls0jddQFl5XeoQNN8QT1WsSCsgBeLAXbjM+XxuZ7L1YRB8opmDu/FCI3AWUBYqcVOT/83sBMBPTWV9D37mO/IPlbp+KjoFWE3cdLlPOVoBgFFeG0qCQmQEKSmiDew6H3gWgBGq3UhIBQPLuLFez4MAZ5r7ZbifTJL6M6XS9MOv7+bKK16QXnubhIH3dRWHCjXE8P/eOUDq6sKQdk8Ta0HrEnOSX3PxBZPjDxF3YdAAAAAElFTkSuQmCC";
                                          }

                                          return (
                                            <div key={file.url} className="cc-file">
                                              <img
                                                src={audioIcon}
                                                alt="AUDIO"
                                                className="cometchat-file-bubble__leading-view"
                                              />
                                              <div className="cc-file-info">
                                                <div
                                                  className="cc-file-name"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >
                                                  {file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}
                                                </div>
                                                <div className="cc-file-meta">
                                                  {formatSize(file.size)} • {file.extension.toLowerCase()}
                                                </div>
                                              </div>
                                              <DownloadIcon
                                                size={20}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.stopPropagation(); // prevent window.open
                                                  e.preventDefault();
                                                  downloadFile(file.url, file.name);
                                                }}
                                                color={!isOutgoing ? "#e09029" : "#ffffff"}
                                              />
                                            </div>
                                          );
                                        } else {
                                          return (
                                            <div
                                              key={file.url}
                                              className="cc-file"
                                            >
                                              <div className="cc-file-thumb">📁</div>
                                              <div className="cc-file-info">
                                                <div className="cc-file-name">{file.name}</div>
                                                <div
                                                  className="cc-file-meta"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >
                                                  {Math.round(file.size / 1024)} KB •{" "}
                                                  {file.extension.toUpperCase()}
                                                </div>
                                              </div>
                                              <DownloadIcon
                                                size={20}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.stopPropagation();      // ✅ prevent window.open
                                                  e.preventDefault();
                                                  downloadFile(file.url, file.name);
                                                }}
                                                color={!isOutgoing ? "#e09029" : "#ffffff"}
                                              />
                                            </div>
                                          );
                                        }
                                      })}
                                    <div className="cc-time">
                                      {new Date(msg.sentAt * 1000).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                      {isOutgoing && (
                                        <span
                                          className={`cometchat-message-bubble__status-info ${getReceiptClass(
                                            msg
                                          )}`}
                                        >
                                          <span className="cometchat-message-list__receipt" />
                                        </span>
                                      )}
                                    </div>
                                    {isOutgoing && hoveredMessageId === msg.id && (
                                      <div
                                        className="cc-info-icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onOpenMessageInfo(msg);
                                        }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="18"
                                          height="18"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <circle cx="12" cy="12" r="10" />
                                          <line x1="12" y1="16" x2="12" y2="12" />
                                          <line x1="12" y1="8" x2="12" y2="8" />
                                        </svg>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          ) : (
                            msg.parentId === undefined && <div className={`cc-bubble ${isOutgoing ? "out" : "in"}`} onClick={() =>
                              isOutgoing && setHoveredMessageId(msg.id)
                            }>
                              {msg.deletedAt ? (
                                <div className="cometchat-message-bubble__body-content-view">
                                  <div className="cometchat">
                                    <div
                                      className={`cometchat-delete-bubble ${isOutgoing
                                        ? "cometchat-delete-bubble-outgoing"
                                        : ""
                                        }`}
                                    >
                                      <div className="cometchat-delete-bubble__body">
                                        <div className="cometchat-delete-bubble__icon"></div>
                                        <label className="cometchat-delete-bubble__text">
                                          This message was deleted
                                        </label>
                                      </div>
                                      <div className="cc-time">
                                        {msg.category === "message" && new Date(msg.sentAt * 1000).toLocaleTimeString([], {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {/* {msg.type === "text" && (
                                    <div className="cc-text">
                                      {renderTextWithMentions(
                                        msg.data?.text || "",
                                        msg.data?.mentions,
                                        loggedInUid,
                                        msg.data?.receiverType
                                      )}
                                    </div>
                                  )} */}
                                  {/* {msg.type === "text" && (
                                  <div className="cc-text">
                                      {renderTextWithMentions(
                                        msg.data?.text || "",
                                        msg.data?.mentions,
                                        loggedInUid,
                                        msg?.receiverType
                                      )}
                                    </div>
                                  )} */}

                                  {msg.type === "text" && (
                                    <div
                                      className={`cc-text ${isEmojiOnly(msg.data?.text || "") ? "cc-text-emoji" : ""
                                        }`}
                                    >
                                      {renderTextWithMentions(
                                        msg.data?.text || "",
                                        msg.data?.mentions,
                                        loggedInUid,
                                        msg?.receiverType,
                                        "text"
                                      )}
                                    </div>
                                  )}

                                  {msg.type === "file" &&
                                    msg.data?.attachments?.map((file) => {
                                      const ext = file.extension.toLowerCase();
                                      const isImage = ["png", "jpg", "jpeg", "webp"].includes(ext);
                                      const isPDF = ext === "pdf";
                                      const isText = ext === "txt";
                                      const isXLSX = ext === "xlsx";
                                      const isDOCX = ext === "docx";
                                      const isPPTX = ext === "pptx";
                                      const isDocument = [
                                        "doc",
                                        "xls",
                                        "ppt"
                                      ].includes(ext);

                                      const videoFormats = ["mp4", "mov", "avi", "mkv", "webm", "flv", "wmv", "m4v"];

                                      const audioFormats = ["mp3", "wav", "ogg", "flac", "aac", "m4a"];

                                      if (isImage) {
                                        return (
                                          <div key={file.url} className="cc-image">
                                            <img
                                              src={file.url}
                                              alt={file.name}
                                              onClick={() =>
                                                setPreviewImage(file.url ?? null)
                                              }
                                            />
                                          </div>
                                        );
                                      } else if (isPDF) {
                                        return (
                                          <div
                                            key={file.url}
                                            className="cc-file"
                                          >
                                            <img
                                              src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI/SURBVHgB1Ve/L2xBFP7m7L2bt7F5b98riO4hCgmFSkWEmmi1Qq3X06j8FXQWtR8lUWyIRiIhGiHBJn6sFXfGOWsb6y5zZ+9d8SWTmcyczPedM2fmngswjDH/uW1zuzXNwapwCreqDgrccmguitz6RcAqDyZqV83lJcz5OaKCenqAbNbWfEcEmA/k+/t4WVwEymVEBc3MIDU6CmQyNuZFCpsN8nkn8gqenqDZAZRKNtY5QhJgclsRyQiIICI5AZYiYhdgDg8RRYSHuAUcHOD6+Bjl9naklKp4KP2vkxNkp6aSFyDI8U24YRF3QQCPyT0ikO+H2jofgWppgRoYqLv+L53GXyYVchEhUQiDcwRofh7U2opgeRl6YyPU5rfnwdcaz9xiFyAR0Ht7APefIVONgKojwPkI9O4uSI7g6upLW1/yIG4Bpuq9OT21sld15t3fgYeHtw16e9EInAXQ2FjFe5qchOrogCvck7CvD8HCAtTICFJzczCcE1IHKL4ZenMTemvLah+nCBCTyhEoDr9qa4PhRCSuASQppZBJzc5a7xUpAnL1aHwcSsJ/dFQ5AsOeGhaj2XN5mFRnJ4KlJes9rQWIdzQ9/Xb/19cRrKy8W5comDoP0qf7WhtyWIVE7n0teSOwjoDmJ1cS7CVG8mgCHMJrg2Qrop8r4IsvXNwCirWT90NDKHE1Exdkr9vBwdA1+TPa5n64duHx4gLFQgFpavyU/K4u/OnuDlta+/afU+JK5UwG3PJoDoR4RziF+xXwDkgcKZ6uuwAAAABJRU5ErkJggg=="
                                              alt="PDF"
                                              // style={{ width: "24px", height: "24px" }}
                                              className="cometchat-file-bubble__leading-view"
                                            />
                                            <div className="cc-file-info">
                                              <div
                                                className="cc-file-name"
                                                onClick={() =>
                                                  window.open(file.url, "_blank")
                                                }
                                              >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                              <div className="cc-file-meta">
                                                {Math.round(file.size / 1024)} KB •{" "}
                                                {file.extension.toLowerCase()}
                                              </div>
                                            </div>
                                            <DownloadIcon
                                              size={20}
                                              style={{ cursor: "pointer" }}
                                              onClick={(e) => {
                                                e.stopPropagation();      // ✅ prevent window.open
                                                e.preventDefault();
                                                downloadFile(file.url, file.name);
                                              }}
                                              color={!isOutgoing ? "#e09029" : "#ffffff"}
                                            />
                                          </div>
                                        );
                                      } else if (isText) {
                                        return (
                                          <div
                                            key={file.url}
                                            className="cc-file"
                                            onClick={() => window.open(file.url, "_blank")}
                                          >
                                            <img
                                              src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAH5SURBVHgB3ZfLSxxBEMa/7s48FkIiG3LYJWxiSMwp5HHJNZBAcgrJHxrMHuJVbyJ7EEEEX+wDcdVdcPGgzmvHqlEPrqvMTPes4g+K7uluur6pLmp6ACKO41dk82SH8WSYZZ/sW1x0lsmmMFkGZJ9YwCx1fl+bHRzjoH+ErNRePIPrWmmXL7CAeHR0c3sPf+tLCMIIWfnx7QM+vn8J236UZvlAjhtdbGzkcs74foiNrW7SpmBKogCyiChEQBYRhQlIK8K4gFb7AFlEpErVLDQ7PbQ6u3j6xIEUEkLKpN3Z7eHn98/FC2BiWOjuH8H3TiClglQKyhrvqhABTKn0mN5cIQy9JAJSCGgLcB0LMzOVW9esb3bhnQZJ33FLkL7EcBhBCKkvgKlVy8mGzOvpCriMNptd+qoI/qphba1zZb1lO4iikKYNRODUC1Cfa2AYDZPnP7++0OYR6v8bEDh3kJy3urotP0tlKAeUssjO+5xgLMCy7BtDfMkNATBVBwTyYkRAfveGBMTIj6EjyC/hYeSATgS0SnGr3UvqvQ5aAlZW2wgCj+5/LvKidQRcXnWcawswwf0U4Do2JilgMDr47s1zSi4fpuC93k6Xx87xn9E8tV9HJ/r9Q7pcdKCkgi7VShm1WnXcVP3Of07pqiZa3CH7h8nAjhfYJ/s+A9xpLS5WrHVdAAAAAElFTkSuQmCC"
                                              alt="TXT"
                                              // style={{ width: "24px", height: "24px" }}
                                              className="cometchat-file-bubble__leading-view"
                                            />
                                            <div className="cc-file-info">
                                              <div
                                                className="cc-file-name"
                                                onClick={() => window.open(file.url, "_blank")}
                                              >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                              <div className="cc-file-meta">
                                                {formatSize(file.size)} • {file.extension.toLowerCase()}
                                              </div>
                                            </div>
                                            <DownloadIcon
                                              size={20}
                                              style={{ cursor: "pointer" }}
                                              onClick={(e) => {
                                                e.stopPropagation();      // ✅ prevent window.open
                                                e.preventDefault();
                                                downloadFile(file.url, file.name);
                                              }}
                                              color={!isOutgoing ? "#e09029" : "#ffffff"}
                                            />
                                          </div>
                                        );
                                      } else if (isXLSX) {
                                        return (
                                          <div
                                            key={file.url}
                                            className="cc-file"
                                          >
                                            <img
                                              src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMRSURBVHgBxVddSBRRFP5m9tfVQhLZqEzTJMkQwwQRIQt6kCiEtF4qeqkgo6B8KurBZ+mHIkoyzIcgsUzChDDcIMjC0MQty8RFDf9zU2TX/e3cu9vm/tU4u04fXGY4d+aeb875zrl3AILX682g0UljzqsMWphP5lvw3/TQSIaysNLYyQi00E156OzUzDxGx39EfTs1ZS2/Ts/OB9lzstYj0aCHRJgYAW+o9UO/BTfrX8LhcEV981bNUU7iyJnbEAQxYD9RWYLSohzo9VpIgFWMZG3r6P2rcwbL2Az6B0aQk2nE9uwNAbttyYlu+gC73QEJSFZDJmrvvYDb5UBz3QWehrNXGgORsNt9JHbtyPhnJGQT2L83HwaDDqa3n7CwsAivxw1B9SegUknIJlC2J49r4NDJaxDJsSiELyWFhGwCdx52wOm0oaa6Eja7C7V17dxuHviOooIsySRkE/g8NMGvudvSqGR/wuNxQRTV6P86hsHhERjXJUEQBXpC4NoYtozjWMXu+BG4er6cUrAGVZcewEVixLJiXnKK+DYyDZdziTsXRRU0msiuZBNITUniBCanrVBr9aSD4KW0ukTShQput5MiIQb1irgQqLrcwL/86f1qKsMFfxkKwYsTMcElUoV4wuZiJpCZboQhQQcz5Xxyas5fhuHLqdRa0oeb0hDnCFw8VcbLsOL0dbadUgo0UZ9lGhBoxJVA26te6HUicrM3kuh8rVkOZBNoN/Xx3DbdPQfzl1HU3GjlYlOMAFM1a71Nz7swMTVLWSANQEECv/GkvdsnwCg5XjUCxytKkJjga62LNgcam99ADmQTKMzLCJyKWCtueGwKa0arSoCh/lEHa/U4sK8QQb14BVi5apahu28YPeYxxALZEXjdNYDDB4uRZEjAu55B8FAoSaC57b1vFwRvhFCrJR1C40eA6g5qjeTjd1TEpIF4ICIBtsspScAaaiwuSOdH7niBrVWUnxZxjv0ZddK1NHRiYnIWH81DfCuNFVs2G7E1c1Okqdb//nMq0lHJwm5oPIMyYI5NzCfz/QsYRIzAPAyESAAAAABJRU5ErkJggg=="
                                              alt="Xlsx"
                                              // style={{ width: "24px", height: "24px" }}
                                              className="cometchat-file-bubble__leading-view"
                                            />
                                            <div className="cc-file-info">
                                              <div
                                                className="cc-file-name"
                                                onClick={() => window.open(file.url, "_blank")}
                                              >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                              <div className="cc-file-meta">
                                                {Math.round(file.size / 1024)} KB •{" "}
                                                {file.extension.toLowerCase()}
                                              </div>
                                            </div>
                                            <DownloadIcon
                                              size={20}
                                              style={{ cursor: "pointer" }}
                                              onClick={(e) => {
                                                e.stopPropagation();      // ✅ prevent window.open
                                                e.preventDefault();
                                                downloadFile(file.url, file.name);
                                              }}
                                              color={!isOutgoing ? "#e09029" : "#ffffff"}
                                            />
                                          </div>
                                        );
                                      } else if (isDOCX) {
                                        return (
                                          <div
                                            key={file.url}
                                            className="cc-file"
                                          >
                                            <img
                                              src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAK7SURBVHgBzZdPTxNBGMafmd0thQoWo0SJiQjqAQSjJkSjiZoQTgb5BHoyxC/B1ZN+C08kolcvEKMSPYgtMRCkFQ0BMREahZb+m/F9t00jbUlmsC08yWQ3M7Pz/OZ98+7OAiStdRe1KWqbujGaZE/2FsWbWWphNFYJapcZYJJuRstHf2xofFvXsFXvGYnWFuPp0wxQ4fJ+XuHxsxzSWVjr0T2JoasuWpqMpidktd7nr9W+zFnJHd5AHsm00fSwRB2USptD1AXABqJuAKYQNQf4FNNWEC5qrMiSxuLXX+hsJ0fhQAhJzcHS92Y8HDlSfwBWKh/GwsoG8pk/ENL1m+cKGmkQAMsLHvONVS5VgqimugH4iwfaoKQHpbJ+Gv4LIBQsXLd37Pqk2wyhyEaKqusaV8HoTQdjd3cvMv7Axfh92plWpb6xERdDV9SueYKisFcKjAHm4hrXLzrklSn1dZ8COtpB7/1cqW+gW+Djwm+6K4PYw8oYIBpXlEeBjrAuGklEvqQRXUzj7EldBBJ0tsgjvqp9SxNZvYhmPlMUevkRuvYJvI2mMLcscaOfw6vQ0yl8KIfyXhcAjsKlHtcH4FBHY3nMLAj093BqNK71SR9KOgHjNS0joDBw3sOJthyUzmF9U2A7JegA4iHUlCU4hqLcC7PdWwNspYD4WqEiYis5KrFACWxsJICllSySGQ+m4bcGYHE1DA824d0/oWaA4cEg3kSS1OdZrWf9JmSz461ZRGJceoWdxtY0Xn3Ywcxcmuo9aLWeNUCMSuzpRJ7SHCKzAsA2pebJBJ3hdJAiYBfUfX0LpFN54pTS7BRa8RwOWIcTINSMhgIkyjvv9G/RQSKJWonXutW3WXWM/4ym6Hq7fGD1ZxKz8wk61tnVdTWdOx3Aha6j1YZeHvjPqaRP7DLfUHuBxoiNp9mTvf8CwVZ4KCLQmwsAAAAASUVORK5CYII="
                                              alt="DOCX"
                                              // style={{ width: "24px", height: "24px" }}
                                              className="cometchat-file-bubble__leading-view"
                                            />
                                            <div className="cc-file-info">
                                              <div
                                                className="cc-file-name"
                                                onClick={() => window.open(file.url, "_blank")}
                                              >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                              <div className="cc-file-meta">
                                                {formatSize(file.size)} • {file.extension.toLowerCase()}
                                              </div>
                                            </div>
                                            <DownloadIcon
                                              size={20}
                                              style={{ cursor: "pointer" }}
                                              onClick={(e) => {
                                                e.stopPropagation();      // ✅ prevent window.open
                                                e.preventDefault();
                                                downloadFile(file.url, file.name);
                                              }}
                                              color={!isOutgoing ? "#e09029" : "#ffffff"}
                                            />
                                          </div>
                                        );
                                      } else if (isPPTX) {
                                        return (
                                          <div
                                            key={file.url}
                                            className="cc-file"
                                          >
                                            <img
                                              src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAJHSURBVHgB1ZdNaxNBGMf/M7uTxHRTqx5K0EIR1HoSv4Dk4KWI4DfwI3jy4En0s4j0Vl8uHtQUvVVMD0HFolLEBg+NTRNtsuvujs/TFLTZFLOzL+I/DNl52fn/ZubZ2VmApLWep1SntK3z0TJ7srfYv1ijNIN81aF0kQGW6eJapLq7CbQ/IbaqF4DS9KStVxhAR4o3XgJPbgH+ALF16SawcBUolCdp3ZFji9fumZmzvF0awIvh/981I5GFYkBkAxADIjuACSHSB/jyCnEgbKQOsIrtz2/hOnOQUsASNEohUGy9R7l2IwcA0jH5Hd+2mugOQtgEoYhCKjW2bSYArONlm8xD9H8SBAFYBJIeAG80f8rr0c7ZArbWDxRPlyQURZkXgpYiTYDLt+Gu1xG6vb2sLFZgz56D1SaAZ3eA/XLWkYKEHehDw914CdzGfVit1xD08ynvqSlUri/BP3sFdnPpQFuOAZ3qEvCNNKU8ut/d0mPWasC1HEge8IjfISuQdB8Y6bVSRdDvIY6MZ0CdqUGcODnMFB3gdA3BVBX+h3qEKxOAwCEz5QwzFHTBu+foNx+jIn5A2pMTGAN4FITqa4OOVMM8P24lC3sbTxwZA5AXSrZAPLuojINQCIHk9oYA/ptHCAZdaGgkldES6Kd3UQg1hEo+A0YAigPASm7OyvZE9P8C0NstT4DOaOHO/CJ2+SWekriv9tzi2Dr+MqLNG7XITe1N7HxcRcFCYtmzCzh66vy4qof//OOUDqxigy8oPUA+YuMV9mTvX6exKJpgmkTmAAAAAElFTkSuQmCC"
                                              alt="PPTX"
                                              className="cometchat-file-bubble__leading-view"
                                            />
                                            <div className="cc-file-info">
                                              <div
                                                className="cc-file-name"
                                                onClick={() => window.open(file.url, "_blank")}
                                              >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                              <div className="cc-file-meta">
                                                {formatSize(file.size)} • {file.extension.toLowerCase()}
                                              </div>
                                            </div>
                                            <DownloadIcon
                                              size={20}
                                              style={{ cursor: "pointer" }}
                                              onClick={(e) => {
                                                e.stopPropagation();      // ✅ prevent window.open
                                                e.preventDefault();
                                                downloadFile(file.url, file.name);
                                              }}
                                              color={!isOutgoing ? "#e09029" : "#ffffff"}
                                            />
                                          </div>
                                        );
                                      } else if (isDocument) {
                                        return (
                                          <div
                                            key={file.url}
                                            className="cc-file"
                                          >
                                            <div className="cc-file-thumb">📄</div>
                                            <div className="cc-file-info">
                                              <div
                                                className="cc-file-name"
                                                onClick={() => window.open(file.url, "_blank")}
                                              >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                              <div className="cc-file-meta">
                                                {Math.round(file.size / 1024)} KB •{" "}
                                                {file.extension.toLowerCase()}
                                              </div>
                                            </div>
                                            <DownloadIcon
                                              size={20}
                                              style={{ cursor: "pointer" }}
                                              onClick={(e) => {
                                                e.stopPropagation();      // ✅ prevent window.open
                                                e.preventDefault();
                                                downloadFile(file.url, file.name);
                                              }}
                                              color={!isOutgoing ? "#e09029" : "#ffffff"}
                                            />
                                          </div>
                                        );
                                      } else if (videoFormats.includes(ext)) {
                                          return (
                                            <div
                                            key={file.url}
                                            className="cc-file"
                                          >
                                            <img
                                              src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI/SURBVHgB1ZfPaxNBFMe/O/tjdtc2JoKgBSXoqXrSk0ftxUNB1KP4H/iHqada0WtSClIVtDdpoYTQQtWqSbYlyf5Isr7ZiIcwxtnsbkq/8Nhlfuz77Hszb2cBUhzHVbI6WTuej9aET+Fb+3OzTVbGfNUhuyUA1ujm4WRv2xvh6NcQaXV1SYdjM9XhGwIgnmzdbUR4+baLaBAjrVbvubh9w4JlaSrDO1LUd5/8mZwLBWGMHXqBMFSaX1aOVVEQhQCkgSgMQBUid4DGfoQ0EAbyBjgYoLnfQnkxgqbp0BgDo+vBoYvVlQvFAwiNtBIOj9oIgmMwZiRmGPJtmToFly/qKJf+P812K7CdyhhANygS8ndNHYFH988lELX3Pupb/aljub0InZwPhxGlgeUD4PBxKFfu2EnFqxHE9pfwn+MN00mioDFd2p9pF4hUPKaIPHmwMDUtTDcTCCkgctDydZPsfJKWrc8+/ECy5bScFuE0ibQ8e1pK1oiqci9E3AJc3oPkIytVrnVgr9nDi9ffcNIzYAmSeQG0OhGer3+lKhgmW8/iJrUqnQeyAfT9ETY/tLD50cModuC4C7TW1BxnBhiH+zuOuwymWaJSO9ujUs96UzuB53l/w81t9XDnArDbGFJp5XAp3NBmdzwzgHCqG2orXEWFnojOLgDn2XObBqAz2XjzWoBB1EdeiuhZy9WutE/8GdXpeney48fPLnb22smBIquWLnFUr1RkXeun/nPKqHQ2xQ3ZK8xHwvGG8Cl8/waJH0DPGwrofQAAAABJRU5ErkJggg=="
                                              alt="VIDEO"
                                              className="cometchat-file-bubble__leading-view"
                                            />
                                            <div className="cc-file-info">
                                              <div
                                                className="cc-file-name"
                                                onClick={() => window.open(file.url, "_blank")}
                                              >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                              <div className="cc-file-meta">
                                                {formatSize(file.size)} • {file.extension.toLowerCase()}
                                              </div>
                                            </div>
                                            <DownloadIcon
                                              size={20}
                                              style={{ cursor: "pointer" }}
                                              onClick={(e) => {
                                                e.stopPropagation();      // ✅ prevent window.open
                                                e.preventDefault();
                                                downloadFile(file.url, file.name);
                                              }}
                                              color={!isOutgoing ? "#e09029" : "#ffffff"}
                                            />
                                          </div>
                                          );
                                        } else if (audioFormats.includes(ext)) {
                                          // Choose icon based on audio type
                                          let audioIcon = "";

                                          if (ext === "ogg") {
                                            // Custom icon for OGG files
                                            audioIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAH6SURBVHgBxVfbasJAEJ3EeL8QURG8QB/EF5/6B/2E/mnbL9B3hf6B9s07jTdUUNM9SxMkTSCb3dgDQ5bdDedkZnZmQ8Rg2/YTsz6zb/sxeAMnuLXfwSczkx4Li9kzBLyxwat39Xw+0/F4JFEUi0VKJBJhtw8gwPbOWpZFk8mEbrcbiaLdblO1WiVd18Nst3x3LRaLSOTA9XrlHxDyfTOUzDhFxCJARERsAsKKUC5gt9uRiAiDYhAwn8/5Kbi3crlMnU4nfgFALpej7XZLh8OB1wTH/BCLAKBUKnFSFDQ8g+pCZAGmaXID4Pb1ev1nTz6f5+SXyyXQA5GSsNVqUbPZpOVyya1er1O32yWfokqZTIabshAgviAcDod0Op0omUzyTO/1enzNr38YhqFOAL5yOp3yBEPjATRNc9eC4OyRFoAv3Gw2lM1m3blGo8Hn0UMgKohMiQAglUq5Y+RDOp2m0WjEQyBCDkhVwkql4uYD4gwThZSAQqHA3Q7ce0UEUoVotVrRfr/nIYgKKQ8g3vBCFNc7kPIAcgA1AJUw5BVMrYDxeMxDAC9EhZQAVDc0HdGjdw/pC4kMuRIBsvAVIPBjoUSA5Z1E+8RFQhXQNYOOKv6M+uz54l3AlWo2m0mdcQdoULVazW/p499/TnWWxV8YMHunxwDEA3CC+wfREERCXNxfDQAAAABJRU5ErkJggg==";
                                          } else {
                                            // Default audio icon for other formats
                                            audioIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI4SURBVHgBxVfPaxNBFP5msttkA8GkuYlo7MGbsj0JXvTsxSgeBIV48ij1L2h79CKIRw/BP0BTUeypZm96M6g9qKBREESsDUTT/NhkfLO20GQ3MNOdTT947GTeZL9v33vzdhYgCCFKZHWybTEb1CSn5Ga7gzdkecwWLbJFKaBGg/Kkd/TrC0Y/NqELvnAOPDuvutyTAsTkrP/+GTrVa0C/A11krj6AfbYClsmpLG/xqNn+y3sHIpcQvTY9wHOIbltleZ4jAeiISESAjojEBKiKMC5g+GEDOiIsGIb/cQNbnxsYFE+BM4YUQ2DOtwYKV+6aF2CfLmPwbm1s7oi/he2vr9AeEAFnZBRqay7y/7FSYJ0pw7lVC66TKGTI0gjILS7ApzDFEsCdPEa/m3BuVKn53Az5c/TQOVvAohRIMy5AYvD6EToPLyN9cRnZ23Wkjrljfsf6b4lEYA/DTx7+LJ9Ef30VmevVUEpsYrGTisB++CSk++QOCbgU8k3hN78NZTSYo/5mT6QT+m/XlNcm2opVoJUCls0jddQFl5XeoQNN8QT1WsSCsgBeLAXbjM+XxuZ7L1YRB8opmDu/FCI3AWUBYqcVOT/83sBMBPTWV9D37mO/IPlbp+KjoFWE3cdLlPOVoBgFFeG0qCQmQEKSmiDew6H3gWgBGq3UhIBQPLuLFez4MAZ5r7ZbifTJL6M6XS9MOv7+bKK16QXnubhIH3dRWHCjXE8P/eOUDq6sKQdk8Ta0HrEnOSX3PxBZPjDxF3YdAAAAAElFTkSuQmCC";
                                          }

                                          return (
                                            <div key={file.url} className="cc-file">
                                              <img
                                                src={audioIcon}
                                                alt="AUDIO"
                                                className="cometchat-file-bubble__leading-view"
                                              />
                                              <div className="cc-file-info">
                                                <div
                                                  className="cc-file-name"
                                                  onClick={() => window.open(file.url, "_blank")}
                                                >
                                                  {file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}
                                                </div>
                                                <div className="cc-file-meta">
                                                  {formatSize(file.size)} • {file.extension.toLowerCase()}
                                                </div>
                                              </div>
                                              <DownloadIcon
                                                size={20}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.stopPropagation(); // prevent window.open
                                                  e.preventDefault();
                                                  downloadFile(file.url, file.name);
                                                }}
                                                color={!isOutgoing ? "#e09029" : "#ffffff"}
                                              />
                                            </div>
                                          );
                                        } else {
                                        return (
                                          <div
                                            key={file.url}
                                            className="cc-file"
                                          >
                                            <div className="cc-file-thumb">📁</div>
                                            <div className="cc-file-info">
                                              <div
                                                className="cc-file-name"
                                                onClick={() => window.open(file.url, "_blank")}
                                              >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                                              <div className="cc-file-meta">
                                                {Math.round(file.size / 1024)} KB •{" "}
                                                {file.extension.toUpperCase()}
                                              </div>
                                            </div>
                                            <DownloadIcon
                                              size={20}
                                              style={{ cursor: "pointer" }}
                                              onClick={(e) => {
                                                e.stopPropagation();      // ✅ prevent window.open
                                                e.preventDefault();
                                                downloadFile(file.url, file.name);
                                              }}
                                              color={!isOutgoing ? "#e09029" : "#ffffff"}
                                            />
                                          </div>
                                        );
                                      }
                                    })
                                  }
                                  {msg.type === "audio" && (
                                    <AudioMessage
                                      msg={msg}
                                      isOutgoing={isOutgoing}
                                      downloadFile={downloadFile}
                                    />
                                  )}
                                  <div className="cc-time">
                                    {new Date(msg.sentAt * 1000).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                    {isOutgoing && (
                                      <span
                                        className={`cometchat-message-bubble__status-info ${getReceiptClass(
                                          msg
                                        )}`}
                                      >
                                        <span className="cometchat-message-list__receipt" />
                                      </span>
                                    )}
                                  </div>
                                  {isOutgoing && hoveredMessageId === msg.id && (
                                    <div
                                      className="cc-info-icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenMessageInfo(msg);
                                      }}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="16" x2="12" y2="12" />
                                        <line x1="12" y1="8" x2="12" y2="8" />
                                      </svg>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        <div>
                          {/* 2. THE THREAD VIEW (Only if replyCount > 0) */}
                          {msg.replyCount !== undefined && msg.replyCount > 0 && (
                            <div className="cometchat-message-bubble__body-thread-view">
                              <div className="cometchat-message-bubble__thread-view-replies">
                                <div className="cometchat">
                                  <button className="cometchat-button" onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenThread(msg);

                                  }}>
                                    <CornerDownRight size={16} strokeWidth={2} style={{ marginRight: 6 }} color="#A1A1A1" />
                                    <label className="cometchat-button__text">
                                      {msg?.replyCount} {msg?.replyCount === 1 ? 'Reply' : 'Replies'}
                                    </label>
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>}
                  </React.Fragment>
                );
              }))}
            {/* Scroll-to-bottom button */}
            {showScrollDown && (
              <button
                className={`cc-scroll-down-button ${showScrollDown ? "cc-scroll-down-button--visible" : ""
                  }`}
                onClick={scrollToBottom}
              >
                {/* You can replace this with your uploaded arrow image */}
                <img
                  src="data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2221%22%20viewBox%3D%220%200%2020%2021%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M9.99841%2012.9961C9.90813%2012.9961%209.82536%2012.9805%209.75008%2012.9492C9.67494%2012.918%209.60376%2012.8665%209.53654%2012.7948L5.55049%208.80859C5.41619%208.67901%205.35195%208.52276%205.35779%208.33984C5.36362%208.15707%205.43362%208.002%205.56779%207.87464C5.70209%207.74727%205.85897%207.68359%206.03841%207.68359C6.21772%207.68359%206.37105%207.74727%206.49841%207.87464L9.99841%2011.3886L13.5124%207.87464C13.6396%207.74727%2013.7906%207.68595%2013.9655%207.69068C14.1402%207.69526%2014.2947%207.76123%2014.429%207.88859C14.5632%208.01582%2014.6309%208.17089%2014.6322%208.3538C14.6333%208.53672%2014.569%208.69297%2014.4395%208.82255L10.4672%2012.7948C10.3954%2012.8665%2010.3219%2012.918%2010.2467%2012.9492C10.1715%2012.9805%2010.0887%2012.9961%209.99841%2012.9961Z%22%20fill%3D%22%23A1A1A1%22%2F%3E%3C%2Fsvg%3E"
                  alt="Scroll down"
                  style={{ width: "24px", height: "24px" }}
                />
              </button>
            )}
          </div>
        </div>
      </div>
      {previewImage && (
        <div
          className="cc-image-preview-overlay"
          onClick={() => setPreviewImage(null)}
        >
          <img src={previewImage} alt="preview" />
          <button
            className="cc-image-close"
            onClick={() => setPreviewImage(null)}
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
};

/* ===================== MESSAGE INFO MODAL ===================== */
// Define the props interface:
type MessageType = "text" | "file" | "audio";

interface MessageInfoModalProps {
  loggedInUid: string;
  message: {
    id: string | number;
    type?: MessageType;
    sentAt?: number;
    readAt?: number;
    deliveredAt?: number;
    receiverType?: "user" | "group";
    data?: {
      text?: string;
      mentions?: Record<string, { name: string }>;
      receiverType?: "user" | "group";
      attachments?: ApiAttachment[];
      entities?: {
        sender?: {
          entity?: {
            name: string;
          };
        };
      };
    };
  };
  onClose: () => void;
}

const MessageInfoModal: React.FC<MessageInfoModalProps> = ({ message, onClose, loggedInUid }) => {
  console.log("message->", message);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // const formatDate = (ts?: number) => {
  //   if (!ts) return "";
  //   return new Date(ts * 1000).toLocaleString("en-GB", {
  //     day: "2-digit",
  //     month: "short",
  //     hour: "2-digit",
  //     minute: "2-digit",
  //     hour12: true,
  //   });
  // };

  const formatDate = (ts?: number) => {
    if (!ts) return "";

    return new Date(ts * 1000)
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace("am", "AM")
      .replace("pm", "PM");
  };

  const formatTime = (ts?: number) => {
    if (!ts) return "";
    return new Date(ts * 1000).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  function getReceiptClass(msg: MessageInfoModalProps["message"]) {
    if (msg.readAt) return "cometchat-message-bubble__status-info-view-receipts-read";
    if (msg.deliveredAt) return "cometchat-message-bubble__status-info-view-receipts-delivered";
    return "cometchat-message-bubble__status-info-view-receipts-sent";
  }

  return (
    <div className="cometchat-message-information__popup-wrapper">
      {/* Main Popup */}
      <div className="cometchat-message-information">

        {/* Header */}
        <div className="cometchat-message-information__header">
          <div className="cometchat-message-information__header-title">Message Information</div>
          <button className="messageinfo-close" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Message Preview */}
        <div className="cometchat-message-information__message">
          <div className="cometchat-message-bubble cometchat-message-bubble-outgoing">
            <div className="cc-bubble out">
              {/* Text Message */}
              {/* {message.type === "text" && (
                <div className="cc-text">
                  {renderTextWithMentions(message.data?.text || "", message.data?.mentions, loggedInUid, message.data?.receiverType)}
                </div>
              )} */}
              {message.type === "text" && (
                <div
                  className={`cc-text ${isEmojiOnly(message.data?.text || "") ? "cc-text-emoji-info" : ""
                    }`}
                >
                  {renderTextWithMentions(
                    message.data?.text || "",
                    message.data?.mentions,
                    loggedInUid,
                    message?.receiverType,
                    "info" // "info"
                  )}
                </div>
              )}

              {/* File / Media Message */}
              {message.type === "file" &&
                message.data?.attachments?.map((file) => {
                  const ext = file.extension.toLowerCase();
                  const isImage = ["png", "jpg", "jpeg", "webp"].includes(ext);
                  const isPDF = ext === "pdf";
                  const isText = ext === "txt";
                  const isXLSX = ext === "xlsx";
                  const isDOCX = ext === "docx";
                  const isPPTX = ext === "pptx";
                  const isDocument = [
                    "doc",
                    "xls",
                    "ppt"
                  ].includes(ext);

                  const videoFormats = ["mp4", "mov", "avi", "mkv", "webm", "flv", "wmv", "m4v"];

                  const audioFormats = ["mp3", "wav", "ogg", "flac", "aac", "m4a"];

                  if (isImage) {
                    return (
                      <div key={file.url} className="cc-image">
                        <img
                          src={file.url}
                          alt={file.name}
                          onClick={() =>
                            setPreviewImage(file.url ?? null)
                          }
                        />
                      </div>
                    );
                  } else if (isPDF) {
                    return (
                      <div
                        key={file.url}
                        className="cc-file"
                      >
                        <img
                          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI/SURBVHgB1Ve/L2xBFP7m7L2bt7F5b98riO4hCgmFSkWEmmi1Qq3X06j8FXQWtR8lUWyIRiIhGiHBJn6sFXfGOWsb6y5zZ+9d8SWTmcyczPedM2fmngswjDH/uW1zuzXNwapwCreqDgrccmguitz6RcAqDyZqV83lJcz5OaKCenqAbNbWfEcEmA/k+/t4WVwEymVEBc3MIDU6CmQyNuZFCpsN8nkn8gqenqDZAZRKNtY5QhJgclsRyQiIICI5AZYiYhdgDg8RRYSHuAUcHOD6+Bjl9naklKp4KP2vkxNkp6aSFyDI8U24YRF3QQCPyT0ikO+H2jofgWppgRoYqLv+L53GXyYVchEhUQiDcwRofh7U2opgeRl6YyPU5rfnwdcaz9xiFyAR0Ht7APefIVONgKojwPkI9O4uSI7g6upLW1/yIG4Bpuq9OT21sld15t3fgYeHtw16e9EInAXQ2FjFe5qchOrogCvck7CvD8HCAtTICFJzczCcE1IHKL4ZenMTemvLah+nCBCTyhEoDr9qa4PhRCSuASQppZBJzc5a7xUpAnL1aHwcSsJ/dFQ5AsOeGhaj2XN5mFRnJ4KlJes9rQWIdzQ9/Xb/19cRrKy8W5comDoP0qf7WhtyWIVE7n0teSOwjoDmJ1cS7CVG8mgCHMJrg2Qrop8r4IsvXNwCirWT90NDKHE1Exdkr9vBwdA1+TPa5n64duHx4gLFQgFpavyU/K4u/OnuDlta+/afU+JK5UwG3PJoDoR4RziF+xXwDkgcKZ6uuwAAAABJRU5ErkJggg=="
                          alt="PDF"
                          // style={{ width: "24px", height: "24px" }}
                          className="cometchat-file-bubble__leading-view"
                        />
                        <div className="cc-file-info">
                          <div
                            className="cc-file-name"
                            onClick={() => window.open(file.url, "_blank")}
                          >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                          <div className="cc-file-meta">
                            {Math.round(file.size / 1024)} KB •{" "}
                            {file.extension.toLowerCase()}
                          </div>
                        </div>
                        <DownloadIcon
                          size={20}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();      // ✅ prevent window.open
                            e.preventDefault();
                            downloadFile(file.url, file.name);
                          }}
                        />
                      </div>
                    );
                  } else if (isText) {
                    return (
                      <div
                        key={file.url}
                        className="cc-file"
                      >
                        <img
                          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAH5SURBVHgB3ZfLSxxBEMa/7s48FkIiG3LYJWxiSMwp5HHJNZBAcgrJHxrMHuJVbyJ7EEEEX+wDcdVdcPGgzmvHqlEPrqvMTPes4g+K7uluur6pLmp6ACKO41dk82SH8WSYZZ/sW1x0lsmmMFkGZJ9YwCx1fl+bHRzjoH+ErNRePIPrWmmXL7CAeHR0c3sPf+tLCMIIWfnx7QM+vn8J236UZvlAjhtdbGzkcs74foiNrW7SpmBKogCyiChEQBYRhQlIK8K4gFb7AFlEpErVLDQ7PbQ6u3j6xIEUEkLKpN3Z7eHn98/FC2BiWOjuH8H3TiClglQKyhrvqhABTKn0mN5cIQy9JAJSCGgLcB0LMzOVW9esb3bhnQZJ33FLkL7EcBhBCKkvgKlVy8mGzOvpCriMNptd+qoI/qphba1zZb1lO4iikKYNRODUC1Cfa2AYDZPnP7++0OYR6v8bEDh3kJy3urotP0tlKAeUssjO+5xgLMCy7BtDfMkNATBVBwTyYkRAfveGBMTIj6EjyC/hYeSATgS0SnGr3UvqvQ5aAlZW2wgCj+5/LvKidQRcXnWcawswwf0U4Do2JilgMDr47s1zSi4fpuC93k6Xx87xn9E8tV9HJ/r9Q7pcdKCkgi7VShm1WnXcVP3Of07pqiZa3CH7h8nAjhfYJ/s+A9xpLS5WrHVdAAAAAElFTkSuQmCC"
                          alt="TXT"
                          // style={{ width: "24px", height: "24px" }}
                          className="cometchat-file-bubble__leading-view"
                        />
                        <div className="cc-file-info">
                          <div
                            className="cc-file-name"
                            onClick={() => window.open(file.url, "_blank")}
                          >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                          <div className="cc-file-meta">
                            {formatSize(file.size)} • {file.extension.toLowerCase()}
                          </div>
                        </div>
                        <DownloadIcon
                          size={20}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();      // ✅ prevent window.open
                            e.preventDefault();
                            downloadFile(file.url, file.name);
                          }}
                        />
                      </div>
                    );
                  } else if (isXLSX) {
                    return (
                      <div
                        key={file.url}
                        className="cc-file"
                      >
                        <img
                          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMRSURBVHgBxVddSBRRFP5m9tfVQhLZqEzTJMkQwwQRIQt6kCiEtF4qeqkgo6B8KurBZ+mHIkoyzIcgsUzChDDcIMjC0MQty8RFDf9zU2TX/e3cu9vm/tU4u04fXGY4d+aeb875zrl3AILX682g0UljzqsMWphP5lvw3/TQSIaysNLYyQi00E156OzUzDxGx39EfTs1ZS2/Ts/OB9lzstYj0aCHRJgYAW+o9UO/BTfrX8LhcEV981bNUU7iyJnbEAQxYD9RWYLSohzo9VpIgFWMZG3r6P2rcwbL2Az6B0aQk2nE9uwNAbttyYlu+gC73QEJSFZDJmrvvYDb5UBz3QWehrNXGgORsNt9JHbtyPhnJGQT2L83HwaDDqa3n7CwsAivxw1B9SegUknIJlC2J49r4NDJaxDJsSiELyWFhGwCdx52wOm0oaa6Eja7C7V17dxuHviOooIsySRkE/g8NMGvudvSqGR/wuNxQRTV6P86hsHhERjXJUEQBXpC4NoYtozjWMXu+BG4er6cUrAGVZcewEVixLJiXnKK+DYyDZdziTsXRRU0msiuZBNITUniBCanrVBr9aSD4KW0ukTShQput5MiIQb1irgQqLrcwL/86f1qKsMFfxkKwYsTMcElUoV4wuZiJpCZboQhQQcz5Xxyas5fhuHLqdRa0oeb0hDnCFw8VcbLsOL0dbadUgo0UZ9lGhBoxJVA26te6HUicrM3kuh8rVkOZBNoN/Xx3DbdPQfzl1HU3GjlYlOMAFM1a71Nz7swMTVLWSANQEECv/GkvdsnwCg5XjUCxytKkJjga62LNgcam99ADmQTKMzLCJyKWCtueGwKa0arSoCh/lEHa/U4sK8QQb14BVi5apahu28YPeYxxALZEXjdNYDDB4uRZEjAu55B8FAoSaC57b1vFwRvhFCrJR1C40eA6g5qjeTjd1TEpIF4ICIBtsspScAaaiwuSOdH7niBrVWUnxZxjv0ZddK1NHRiYnIWH81DfCuNFVs2G7E1c1Okqdb//nMq0lHJwm5oPIMyYI5NzCfz/QsYRIzAPAyESAAAAABJRU5ErkJggg=="
                          alt="Xlsx"
                          // style={{ width: "24px", height: "24px" }}
                          className="cometchat-file-bubble__leading-view"
                        />
                        <div className="cc-file-info">
                          <div
                            className="cc-file-name"
                            onClick={() => window.open(file.url, "_blank")}
                          >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                          <div className="cc-file-meta">
                            {Math.round(file.size / 1024)} KB •{" "}
                            {file.extension.toLowerCase()}
                          </div>
                        </div>
                        <DownloadIcon
                          size={20}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();      // ✅ prevent window.open
                            e.preventDefault();
                            downloadFile(file.url, file.name);
                          }}
                        />
                      </div>
                    );
                  } else if (isDOCX) {
                    return (
                      <div
                        key={file.url}
                        className="cc-file"
                      >
                        <img
                          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAK7SURBVHgBzZdPTxNBGMafmd0thQoWo0SJiQjqAQSjJkSjiZoQTgb5BHoyxC/B1ZN+C08kolcvEKMSPYgtMRCkFQ0BMREahZb+m/F9t00jbUlmsC08yWQ3M7Pz/OZ98+7OAiStdRe1KWqbujGaZE/2FsWbWWphNFYJapcZYJJuRstHf2xofFvXsFXvGYnWFuPp0wxQ4fJ+XuHxsxzSWVjr0T2JoasuWpqMpidktd7nr9W+zFnJHd5AHsm00fSwRB2USptD1AXABqJuAKYQNQf4FNNWEC5qrMiSxuLXX+hsJ0fhQAhJzcHS92Y8HDlSfwBWKh/GwsoG8pk/ENL1m+cKGmkQAMsLHvONVS5VgqimugH4iwfaoKQHpbJ+Gv4LIBQsXLd37Pqk2wyhyEaKqusaV8HoTQdjd3cvMv7Axfh92plWpb6xERdDV9SueYKisFcKjAHm4hrXLzrklSn1dZ8COtpB7/1cqW+gW+Djwm+6K4PYw8oYIBpXlEeBjrAuGklEvqQRXUzj7EldBBJ0tsgjvqp9SxNZvYhmPlMUevkRuvYJvI2mMLcscaOfw6vQ0yl8KIfyXhcAjsKlHtcH4FBHY3nMLAj093BqNK71SR9KOgHjNS0joDBw3sOJthyUzmF9U2A7JegA4iHUlCU4hqLcC7PdWwNspYD4WqEiYis5KrFACWxsJICllSySGQ+m4bcGYHE1DA824d0/oWaA4cEg3kSS1OdZrWf9JmSz461ZRGJceoWdxtY0Xn3Ywcxcmuo9aLWeNUCMSuzpRJ7SHCKzAsA2pebJBJ3hdJAiYBfUfX0LpFN54pTS7BRa8RwOWIcTINSMhgIkyjvv9G/RQSKJWonXutW3WXWM/4ym6Hq7fGD1ZxKz8wk61tnVdTWdOx3Aha6j1YZeHvjPqaRP7DLfUHuBxoiNp9mTvf8CwVZ4KCLQmwsAAAAASUVORK5CYII="
                          alt="DOCX"
                          className="cometchat-file-bubble__leading-view"
                        />
                        <div className="cc-file-info">
                          <div
                            className="cc-file-name"
                            onClick={() => window.open(file.url, "_blank")}
                          >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                          <div className="cc-file-meta">
                            {Math.round(file.size / 1024)} KB •{" "}
                            {file.extension.toLowerCase()}
                          </div>
                        </div>
                        <DownloadIcon
                          size={20}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();      // ✅ prevent window.open
                            e.preventDefault();
                            downloadFile(file.url, file.name);
                          }}
                        />
                      </div>
                    );
                  } else if (isPPTX) {
                    return (
                      <div
                        key={file.url}
                        className="cc-file"
                      >
                        <img
                          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAJHSURBVHgB1ZdNaxNBGMf/M7uTxHRTqx5K0EIR1HoSv4Dk4KWI4DfwI3jy4En0s4j0Vl8uHtQUvVVMD0HFolLEBg+NTRNtsuvujs/TFLTZFLOzL+I/DNl52fn/ZubZ2VmApLWep1SntK3z0TJ7srfYv1ijNIN81aF0kQGW6eJapLq7CbQ/IbaqF4DS9KStVxhAR4o3XgJPbgH+ALF16SawcBUolCdp3ZFji9fumZmzvF0awIvh/981I5GFYkBkAxADIjuACSHSB/jyCnEgbKQOsIrtz2/hOnOQUsASNEohUGy9R7l2IwcA0jH5Hd+2mugOQtgEoYhCKjW2bSYArONlm8xD9H8SBAFYBJIeAG80f8rr0c7ZArbWDxRPlyQURZkXgpYiTYDLt+Gu1xG6vb2sLFZgz56D1SaAZ3eA/XLWkYKEHehDw914CdzGfVit1xD08ynvqSlUri/BP3sFdnPpQFuOAZ3qEvCNNKU8ut/d0mPWasC1HEge8IjfISuQdB8Y6bVSRdDvIY6MZ0CdqUGcODnMFB3gdA3BVBX+h3qEKxOAwCEz5QwzFHTBu+foNx+jIn5A2pMTGAN4FITqa4OOVMM8P24lC3sbTxwZA5AXSrZAPLuojINQCIHk9oYA/ptHCAZdaGgkldES6Kd3UQg1hEo+A0YAigPASm7OyvZE9P8C0NstT4DOaOHO/CJ2+SWekriv9tzi2Dr+MqLNG7XITe1N7HxcRcFCYtmzCzh66vy4qof//OOUDqxigy8oPUA+YuMV9mTvX6exKJpgmkTmAAAAAElFTkSuQmCC"
                          alt="PPTX"
                          className="cometchat-file-bubble__leading-view"
                        />
                        <div className="cc-file-info">
                          <div
                            className="cc-file-name"
                            onClick={() => window.open(file.url, "_blank")}
                          >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                          <div className="cc-file-meta">
                            {formatSize(file.size)} • {file.extension.toLowerCase()}
                          </div>
                        </div>
                        <DownloadIcon
                          size={20}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();      // ✅ prevent window.open
                            e.preventDefault();
                            downloadFile(file.url, file.name);
                          }}
                        />
                      </div>
                    );
                  } else if (isDocument) {
                    return (
                      <div
                        key={file.url}
                        className="cc-file"
                      >
                        <div className="cc-file-thumb">📄</div>
                        <div className="cc-file-info">
                          <div
                            className="cc-file-name"
                            onClick={() => window.open(file.url, "_blank")}
                          >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                          <div
                            className="cc-file-meta"
                            onClick={() => window.open(file.url, "_blank")}
                          >
                            {Math.round(file.size / 1024)} KB •{" "}
                            {file.extension.toLowerCase()}
                          </div>
                        </div>
                        <DownloadIcon
                          size={20}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();      // ✅ prevent window.open
                            e.preventDefault();
                            downloadFile(file.url, file.name);
                          }}
                        />
                      </div>
                    );
                  } else if (videoFormats.includes(ext)) {
                      return (
                        <div
                        key={file.url}
                        className="cc-file"
                      >
                        <img
                          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI/SURBVHgB1ZfPaxNBFMe/O/tjdtc2JoKgBSXoqXrSk0ftxUNB1KP4H/iHqada0WtSClIVtDdpoYTQQtWqSbYlyf5Isr7ZiIcwxtnsbkq/8Nhlfuz77Hszb2cBUhzHVbI6WTuej9aET+Fb+3OzTVbGfNUhuyUA1ujm4WRv2xvh6NcQaXV1SYdjM9XhGwIgnmzdbUR4+baLaBAjrVbvubh9w4JlaSrDO1LUd5/8mZwLBWGMHXqBMFSaX1aOVVEQhQCkgSgMQBUid4DGfoQ0EAbyBjgYoLnfQnkxgqbp0BgDo+vBoYvVlQvFAwiNtBIOj9oIgmMwZiRmGPJtmToFly/qKJf+P812K7CdyhhANygS8ndNHYFH988lELX3Pupb/aljub0InZwPhxGlgeUD4PBxKFfu2EnFqxHE9pfwn+MN00mioDFd2p9pF4hUPKaIPHmwMDUtTDcTCCkgctDydZPsfJKWrc8+/ECy5bScFuE0ibQ8e1pK1oiqci9E3AJc3oPkIytVrnVgr9nDi9ffcNIzYAmSeQG0OhGer3+lKhgmW8/iJrUqnQeyAfT9ETY/tLD50cModuC4C7TW1BxnBhiH+zuOuwymWaJSO9ujUs96UzuB53l/w81t9XDnArDbGFJp5XAp3NBmdzwzgHCqG2orXEWFnojOLgDn2XObBqAz2XjzWoBB1EdeiuhZy9WutE/8GdXpeney48fPLnb22smBIquWLnFUr1RkXeun/nPKqHQ2xQ3ZK8xHwvGG8Cl8/waJH0DPGwrofQAAAABJRU5ErkJggg=="
                          alt="VIDEO"
                          className="cometchat-file-bubble__leading-view"
                        />
                        <div className="cc-file-info">
                          <div
                            className="cc-file-name"
                            onClick={() => window.open(file.url, "_blank")}
                          >{file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}</div>
                          <div className="cc-file-meta">
                            {formatSize(file.size)} • {file.extension.toLowerCase()}
                          </div>
                        </div>
                        <DownloadIcon
                          size={20}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();      // ✅ prevent window.open
                            e.preventDefault();
                            downloadFile(file.url, file.name);
                          }}
                        />
                      </div>
                      );
                  } else if (audioFormats.includes(ext)) {
                      // Choose icon based on audio type
                      let audioIcon = "";

                      if (ext === "ogg") {
                        // Custom icon for OGG files
                        audioIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAH6SURBVHgBxVfbasJAEJ3EeL8QURG8QB/EF5/6B/2E/mnbL9B3hf6B9s07jTdUUNM9SxMkTSCb3dgDQ5bdDedkZnZmQ8Rg2/YTsz6zb/sxeAMnuLXfwSczkx4Li9kzBLyxwat39Xw+0/F4JFEUi0VKJBJhtw8gwPbOWpZFk8mEbrcbiaLdblO1WiVd18Nst3x3LRaLSOTA9XrlHxDyfTOUzDhFxCJARERsAsKKUC5gt9uRiAiDYhAwn8/5Kbi3crlMnU4nfgFALpej7XZLh8OB1wTH/BCLAKBUKnFSFDQ8g+pCZAGmaXID4Pb1ev1nTz6f5+SXyyXQA5GSsNVqUbPZpOVyya1er1O32yWfokqZTIabshAgviAcDod0Op0omUzyTO/1enzNr38YhqFOAL5yOp3yBEPjATRNc9eC4OyRFoAv3Gw2lM1m3blGo8Hn0UMgKohMiQAglUq5Y+RDOp2m0WjEQyBCDkhVwkql4uYD4gwThZSAQqHA3Q7ce0UEUoVotVrRfr/nIYgKKQ8g3vBCFNc7kPIAcgA1AJUw5BVMrYDxeMxDAC9EhZQAVDc0HdGjdw/pC4kMuRIBsvAVIPBjoUSA5Z1E+8RFQhXQNYOOKv6M+uz54l3AlWo2m0mdcQdoULVazW/p499/TnWWxV8YMHunxwDEA3CC+wfREERCXNxfDQAAAABJRU5ErkJggg==";
                      } else {
                        // Default audio icon for other formats
                        audioIcon = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI4SURBVHgBxVfPaxNBFP5msttkA8GkuYlo7MGbsj0JXvTsxSgeBIV48ij1L2h79CKIRw/BP0BTUeypZm96M6g9qKBREESsDUTT/NhkfLO20GQ3MNOdTT947GTeZL9v33vzdhYgCCFKZHWybTEb1CSn5Ga7gzdkecwWLbJFKaBGg/Kkd/TrC0Y/NqELvnAOPDuvutyTAsTkrP/+GTrVa0C/A11krj6AfbYClsmpLG/xqNn+y3sHIpcQvTY9wHOIbltleZ4jAeiISESAjojEBKiKMC5g+GEDOiIsGIb/cQNbnxsYFE+BM4YUQ2DOtwYKV+6aF2CfLmPwbm1s7oi/he2vr9AeEAFnZBRqay7y/7FSYJ0pw7lVC66TKGTI0gjILS7ApzDFEsCdPEa/m3BuVKn53Az5c/TQOVvAohRIMy5AYvD6EToPLyN9cRnZ23Wkjrljfsf6b4lEYA/DTx7+LJ9Ef30VmevVUEpsYrGTisB++CSk++QOCbgU8k3hN78NZTSYo/5mT6QT+m/XlNcm2opVoJUCls0jddQFl5XeoQNN8QT1WsSCsgBeLAXbjM+XxuZ7L1YRB8opmDu/FCI3AWUBYqcVOT/83sBMBPTWV9D37mO/IPlbp+KjoFWE3cdLlPOVoBgFFeG0qCQmQEKSmiDew6H3gWgBGq3UhIBQPLuLFez4MAZ5r7ZbifTJL6M6XS9MOv7+bKK16QXnubhIH3dRWHCjXE8P/eOUDq6sKQdk8Ta0HrEnOSX3PxBZPjDxF3YdAAAAAElFTkSuQmCC";
                      }

                      return (
                        <div key={file.url} className="cc-file">
                          <img
                            src={audioIcon}
                            alt="AUDIO"
                            className="cometchat-file-bubble__leading-view"
                          />
                          <div className="cc-file-info">
                            <div
                              className="cc-file-name"
                              onClick={() => window.open(file.url, "_blank")}
                            >
                              {file.name.length > 15 ? file.name.slice(0, 15) + "..." : file.name}
                            </div>
                            <div className="cc-file-meta">
                              {formatSize(file.size)} • {file.extension.toLowerCase()}
                            </div>
                          </div>
                          <DownloadIcon
                            size={20}
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation(); // prevent window.open
                              e.preventDefault();
                              downloadFile(file.url, file.name);
                            }}
                          />
                        </div>
                      );
                    } else {
                    return (
                      <div
                        key={file.url}
                        className="cc-file"
                      >
                        <div className="cc-file-thumb">📁</div>
                        <div className="cc-file-info">
                          <div className="cc-file-name">{file.name}</div>
                          <div
                            className="cc-file-meta"
                            onClick={() => window.open(file.url, "_blank")}
                          >
                            {Math.round(file.size / 1024)} KB •{" "}
                            {file.extension.toUpperCase()}
                          </div>
                        </div>
                        <DownloadIcon
                          size={20}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();      // ✅ prevent window.open
                            e.preventDefault();
                            downloadFile(file.url, file.name);
                          }}
                        />
                      </div>
                    );
                  }
                })
              }
              {message.type === "audio" && (
                <AudioMessage
                  msg={message}
                  isOutgoing={false}
                  downloadFile={downloadFile}
                />
              )}
              <div className="cc-time">
                {formatTime(message.sentAt)}
                <span
                  className={`cometchat-message-bubble__status-info ${getReceiptClass(message as ApiMessage)}`}
                >
                  <span className="cometchat-message-list__receipt" />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Receipts Section */}
        {/* <div className="cometchat-message-information__receipts">
          {["readAt", "deliveredAt", "sentAt"].map((statusKey) => {
            const statusName =
              statusKey === "readAt" ? "Read" :
                statusKey === "deliveredAt" ? "Delivered" :
                  "Sent";

            const statusValue = message[statusKey as keyof typeof message];

            if (!statusValue) return null;

            return (
              <div key={statusKey} className="cometchat" style={{ width: "100%", height: "fit-content" }}>
                <div className="cometchat-list-item" id={statusName.toLowerCase()}>
                  <div className="cometchat-list-item__body">
                    <div className="cometchat-list-item__title-container">
                      <div className="cometchat-list-item__body-title">{statusName}</div>
                      <div className="cometchat-list-item__body-subtitle">
                        <div className="cometchat-message-information__receipts-subtitle-text">
                          <div className="cometchat">
                            {typeof statusValue === "number"
                              ? formatDate(statusValue)
                              : typeof statusValue === "string"
                                ? formatDate(Number(statusValue))
                                : ""}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="cometchat-list-item__trailing-view"></div>
                    <div className="cometchat-list-item__trailing-view-hidden"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div> */}

        {/* Receipts Section */}
        <MessageReceipts message={message} formatDate={formatDate} />

        {/* Image Preview Overlay */}
        {previewImage && (
          <div className="cc-image-preview-overlay" onClick={() => setPreviewImage(null)}>
            <img src={previewImage} alt="preview" />
            <button className="cc-image-close" onClick={() => setPreviewImage(null)}>✕</button>
          </div>
        )}
      </div>
    </div>
  );
};

type AvatarProps = {
  name?: string;
};

const getInitials = (name: string = "") => {
  const parts = name.trim().split(/\s+/);

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Avatar: React.FC<AvatarProps> = ({ name = "" }) => {
  const initials = getInitials(name);

  return (
    <div
      className="cometchat"
      style={{
        height: "inherit",
        width: "inherit",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div className="cometchat-avatar" style={{ height: "32px", width: "32px" }}>
        <span className="cometchat-avatar__text">{initials}</span>
      </div>
    </div>
  );
};

interface ChatHistoryAvatarProps {
  name?: string;
  size?: number;
}

export const getHistoryAvatarInitials = (name: string = "") => {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) return "?";

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return (words[0][0] + words[1][0]).toUpperCase();
};

const ChatHistoryAvatar: React.FC<ChatHistoryAvatarProps> = ({
  name = "",
  size = 40,
}) => {
  const initials = getHistoryAvatarInitials(name);

  return (
    <div
      className="cc-history-avatar"
      style={{
        height: size,
        width: size,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: size / 2.2,
        flexShrink: 0,
        font: "var(--cometchat-font-heading3-bold, 700 20px Roboto)"
      }}
    >
      {initials}
    </div>
  );
};

interface MessageReceiptsInfoModalProps {
  message: {
    id: string | number;
    type?: MessageType;
    sentAt?: number;
    readAt?: number;
    deliveredAt?: number;
    receiverType?: "user" | "group";
    messageInfo?: {
      receipts?: GroupReceipt[];
    };
    data?: {
      text?: string;
      mentions?: Record<string, { name: string }>;
      receiverType?: "user" | "group";
      attachments?: ApiAttachment[];
      entities?: {
        sender?: {
          entity?: {
            name: string;
          };
        };
      };
    };
  };
  formatDate: (timestamp: number) => string;
}

// type GroupReceipt = {
//   deliveredAt?: number;
//   readAt?: number;
//   recipient: {
//     uid: string;
//     name: string;
//     avatar: string;
//   };
// };

type GroupReceipt = {
  deliveredAt?: number;
  readAt?: number;
  recipient: {
    uid: string;
    name: string;
    avatar?: string;
    role?: string;
    status?: string;
    createdAt?: number;
    updatedAt?: number;
    lastActiveAt?: number;
    metadata?: {
      "@private"?: {
        email?: string;
      };
    };
  };
};

const MessageReceipts: React.FC<MessageReceiptsInfoModalProps> = ({
  message,
  formatDate,
}) => {
  const [receipts, setReceipts] = React.useState<GroupReceipt[]>([]);

  /* ============================
     FETCH GROUP RECEIPTS
     ============================ */
  // React.useEffect(() => {
  //   if (!message?.id || message.receiverType !== "group") return;

  //   CometChat.getMessageDetails(message.id)
  //     .then((resp: any) => {
  //       setReceipts(resp.receipts.data ?? []);
  //     })
  //     .catch(console.error);
  // }, [message?.id, message?.receiverType]);

  React.useEffect(() => {
    if (
      message?.receiverType !== "group" ||
      !message?.messageInfo?.receipts
    ) {
      setReceipts([]);
      return;
    }

    setReceipts(message.messageInfo.receipts);
  }, [message?.id, message?.receiverType]);

  /* ============================
    REALTIME UPDATES
    ============================ */
  React.useEffect(() => {
    if (!message?.id || message.receiverType !== "group") return;

    const listenerId = `receipt_listener_${message.id}`;

    CometChat.addMessageListener(
      listenerId,
      new CometChat.MessageListener({
        onMessagesRead: (receipt: any) => {
          setReceipts((prev) => {
            const others = prev.filter(
              (r) => r.recipient.uid !== receipt.recipient.uid
            );

            return [
              ...others,
              {
                recipient: receipt.recipient,
                deliveredAt: receipt.deliveredAt,
                readAt: receipt.readAt,
              },
            ];
          });
        },

        onMessagesDelivered: (receipt: any) => {
          setReceipts((prev) => {
            const others = prev.filter(
              (r) => r.recipient.uid !== receipt.recipient.uid
            );

            return [
              ...others,
              {
                recipient: receipt.recipient,
                deliveredAt: receipt.deliveredAt,
              },
            ];
          });
        },
      })
    );

    return () => {
      CometChat.removeMessageListener(listenerId);
    };
  }, [message?.id, message?.receiverType]);

  /* ============================
    JSX
  ============================ */
  return (
    <div className="cometchat-message-information__receipts">
      {message.receiverType === "group" ? (
        <div className="cometchat-list__item-wrapper">
          {receipts.map((r) => (
            <div
              key={r.recipient.uid}
              className="cometchat"
              style={{ width: "100%", height: "fit-content" }}
            >
              <div className="cometchat-list-item">
                {/* Avatar */}
                {/* <div className="cometchat-list-item__leading-view">
                  <div
                    className="cometchat"
                    style={{
                      height: "inherit",
                      width: "inherit",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div className="cometchat-avatar">
                      <span className="cometchat-avatar__text">
                        {r.recipient.name.slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div> */}
                <div className="cometchat-list-item__leading-view">
                  <div
                    className="cometchat"
                    style={{
                      height: "inherit",
                      width: "inherit",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div className="cometchat-avatar">
                      {r.recipient.avatar ? (
                        <img
                          src={r.recipient.avatar}
                          alt={r.recipient.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <span className="cometchat-avatar__text">
                          {/* {r.recipient.name.slice(0, 2).toUpperCase()} */}
                          <Avatar name={r.recipient.name} />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {/* Body */}
                <div className="cometchat-list-item__body">
                  <div className="cometchat-list-item__title-container">
                    {/* User Name */}
                    <div className="cometchat-list-item__body-title">
                      {r.recipient.name}
                    </div>

                    {/* Read / Delivered */}
                    <div className="cometchat-list-item__body-subtitle">
                      <div
                        className="cometchat-message-information__receipts-subtitle"
                        style={{ width: "100%" }}
                      >
                        {/* READ */}
                        {r.readAt && (
                          <div
                            className="cometchat-message-information__receipts-subtitle-text"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <div>Read</div>
                            <div className="cometchat-date">
                              {formatDate(r.readAt)}
                            </div>
                          </div>
                        )}

                        {/* DELIVERED */}
                        {r.deliveredAt && (
                          <div
                            className="cometchat-message-information__receipts-subtitle-text"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <div>Delivered</div>
                            <div className="cometchat-date">
                              {formatDate(r.deliveredAt)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="cometchat-list-item__trailing-view" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* =======================
        1-TO-1 MESSAGE RECEIPTS
        ======================= */
        ["readAt", "deliveredAt", "sentAt"].map((statusKey) => {
          const statusName =
            statusKey === "readAt"
              ? "Read"
              : statusKey === "deliveredAt"
                ? "Delivered"
                : "Sent";

          const statusValue = message[statusKey as keyof typeof message];
          if (!statusValue) return null;

          return (
            <div
              key={statusKey}
              className="cometchat"
              style={{ width: "100%", height: "fit-content" }}
            >
              <div
                className="cometchat-list-item"
                id={statusName.toLowerCase()}
              >
                <div className="cometchat-list-item__body">
                  <div className="cometchat-list-item__title-container">
                    <div className="cometchat-list-item__body-title">
                      {statusName}
                    </div>

                    <div className="cometchat-list-item__body-subtitle">
                      <div className="cometchat-message-information__receipts-subtitle-text">
                        <div className="cometchat-date-one-info">
                          {typeof statusValue === "number"
                            ? formatDate(statusValue)
                            : formatDate(Number(statusValue))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="cometchat-list-item__trailing-view" />
                  <div className="cometchat-list-item__trailing-view-hidden" />
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

type Props = {
  msg: any;
  isOutgoing: boolean;
  downloadFile: (url: string, name: string) => void;
};

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const AudioMessage: React.FC<Props> = ({
  msg,
  isOutgoing,
  downloadFile
}) => {
  const file = msg.data.attachments[0];

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    playing ? audioRef.current.pause() : audioRef.current.play();
  };

  const seekAudio = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (!audioRef.current || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = clickX / rect.width;

    const newTime = percent * duration;

    audioRef.current.currentTime = newTime;
    setCurrent(newTime);

    audioRef.current.play();
  };

  return (
    <div className="cometchat-message-bubble__body-wrapper">
      <div className="cometchat-message-bubble__body cometchat-message-bubble__audio-message">
        <div className="cometchat-message-bubble__body-content-view">

          {/* 🔊 AUDIO BUBBLE */}
          <div
            className={`cometchat-audio-bubble ${isOutgoing
              ? "cometchat-audio-bubble-outgoing"
              : "cometchat-audio-bubble-incoming"
              }`}
          >
            {/* LEFT SIDE */}
            <div>
              {/* ▶ PLAY / PAUSE */}
              <div
                className="cometchat-audio-bubble__leading-view"
                onClick={toggle}
              >
                <div
                  className={
                    playing
                      ? "cometchat-audio-bubble__leading-view-pause"
                      : "cometchat-audio-bubble__leading-view-play"
                  }
                />
              </div>

              {/* 🌊 WAVE + TIME */}
              <div className="cometchat-audio-bubble__body">
                <div className="cometchat-audio-bubble__body-wave" onClick={seekAudio}>
                  {Array.from({ length: 24 }).map((_, i) => {
                    const progress = duration ? current / duration : 0;
                    const active = i / 24 <= progress;

                    return (
                      <span
                        key={i}
                        className={`wave-bar ${active ? "active" : ""}`}
                      />
                    );
                  })}
                </div>


                <div className="cometchat-audio-bubble__body-time">
                  {formatTime(current)} / {formatTime(duration)}
                </div>
              </div>
            </div>

            {/* ⬇ DOWNLOAD */}
            <DownloadIcon
              size={20}
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();      // ✅ prevent window.open
                e.preventDefault();
                downloadFile(file.url, file.name);
              }}
              color={!isOutgoing ? "#1F6FD6" : "#ffffff"}
            />

            {/* 🎧 AUDIO ELEMENT */}
            <audio
              ref={audioRef}
              src={file.url}
              preload="metadata"
              onLoadedMetadata={(e) =>
                setDuration(e.currentTarget.duration || 0)
              }
              onTimeUpdate={(e) =>
                setCurrent(e.currentTarget.currentTime)
              }
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => {
                setPlaying(false);
                setCurrent(0);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioMessage;

export interface ThreadModalProps {
  message: ApiMessage;
  replies?: ApiMessage[];
  loading?: boolean;
  onOpenMessageInfo: (msg: ApiMessage) => void;
  onClose: () => void;
  loggedInUid: string;
  apiMessages: any;
}

export interface MessageBubbleProps {
  msg: ApiMessage;
  loggedInUid: string;
  apiMessages: ApiMessage[]; // ✅ ARRAY
  onOpenMessageInfo: (msg: ApiMessage) => void;
}

export const ThreadModal: React.FC<ThreadModalProps> = ({
  message,
  replies = [],
  loading,
  onOpenMessageInfo,
  onClose,
  loggedInUid,
  apiMessages
}) => {

  // show only real messages
  // const validReplies = replies.filter(
  //   (msg) => msg.category === "message"
  // );

  const threadScrollRef = useRef<HTMLDivElement>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);

  const validReplies = (replies || [])
    .filter(
      (msg: any) =>
        msg?.category === "message" &&
        // Number(msg?.parentMessageId) === Number(message?.id)
        Number(msg?.parentId) === Number(message?.id)
    )
    .map((msg: any) => ({
      ...msg,
      messageInfo: msg?.messageInfo ?? {}
    }));

  // Sort messages ascending by sentAt timestamp
  const sortedReplies = [...validReplies].sort((a, b) => a.sentAt - b.sentAt);

  // Show/hide down arrow based on scroll
  const handleThreadScroll = () => {
    const el = threadScrollRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);

    setShowScrollDown(distanceFromBottom > 40);
  };
  
  // Scroll to bottom
  const scrollToBottom = () => {
    if (!threadScrollRef.current) return;

    threadScrollRef.current.scrollTo({
      top: threadScrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  const prevReplyCountRef = useRef(validReplies.length);

  useEffect(() => {
    if (!threadScrollRef.current) return;

    const prevCount = prevReplyCountRef.current;
    const newCount = validReplies.length;

    // scroll only when new reply added
    if (newCount > prevCount && !showScrollDown) {
      threadScrollRef.current.scrollTo({
        top: threadScrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }

    prevReplyCountRef.current = newCount;
  }, [validReplies.length, showScrollDown]);

  const senderId =
    typeof message.sender === 'object' && message.sender !== null
      ? message?.data?.entities?.sender?.entity?.uid
      : message.sender;

  const formatChatDate = (unixSeconds: number) => {
    const msgDate = new Date(unixSeconds * 1000);
    const today = new Date();

    const isToday =
      msgDate.getDate() === today.getDate() &&
      msgDate.getMonth() === today.getMonth() &&
      msgDate.getFullYear() === today.getFullYear();

    if (isToday) return "Today";

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isYesterday =
      msgDate.getDate() === yesterday.getDate() &&
      msgDate.getMonth() === yesterday.getMonth() &&
      msgDate.getFullYear() === yesterday.getFullYear();

    if (isYesterday) return "Yesterday";

    const day = String(msgDate.getDate()).padStart(2, "0");
    const month = msgDate.toLocaleString("en-US", { month: "short" });
    const year = msgDate.getFullYear();

    return `${day} ${month}, ${year}`;
  };

  const date = formatChatDate(message.sentAt);

  const index = apiMessages.findIndex((m: any) => m.id === message.id);

  const prevDate =
    index > 0 && apiMessages[index - 1]
      ? formatChatDate(apiMessages[index - 1].sentAt)
      : null;

  const ChatHistorySkeleton = () => {
    // We'll alternate in/out for visual effect
    const rows = Array.from({ length: 8 });

    return (
      <>
        {rows.map((_, i) => {
          const isIncoming = i % 2 === 0; // alternate between in/out
          return (
            <div key={i} className={`cc-row ${isIncoming ? "in" : "out"}`}>
              <div className="cc-avatar-wrapper">
                <div className="skeleton-avatar shimmer" />
              </div>

              <div className={`cc-bubble ${isIncoming ? "in" : "out"}`}>
                <div className="skeleton-line short shimmer" />
                <div className="skeleton-line long shimmer" />
              </div>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="thread-modal-overlay">
      <div className="thread-modal">

        {/* Header */}
        <div className="thread-modal-header">
          <div>
            <h3>Thread</h3>
            <span className="thread-subtitle">{message?.data?.entities?.sender?.entity?.name}</span>
          </div>
          <button className="chathistory-close" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Body */}
        <div className="thread-modal-body">

          {/* Parent message */}
          {message.category === "message" && (
            <MessageBubble
              msg={message}
              loggedInUid={loggedInUid}
              apiMessages={apiMessages}
              onOpenMessageInfo={onOpenMessageInfo}
            />
          )}

          {/* Replies section */}
          {loading ? (
            <ChatHistorySkeleton />
          ) : (
            <>
              <div className="thread-replies-label cometchat-thread-header__reply-bar">
                <div className="cometchat-thread-header__reply-bar-count">
                  {validReplies.length} Replies
                </div>
                <div className="cometchat-thread-header__reply-bar-divider"></div>
              </div>

              {/* {message.parentId === undefined && date !== prevDate && (
                <div className="cc-date-separator">{date}</div>
              )} */}

              {/* {validReplies.map((reply) => (
                <MessageBubble
                  key={reply.id}
                  msg={reply}
                  loggedInUid={loggedInUid}
                  apiMessages={apiMessages}
                  onOpenMessageInfo={onOpenMessageInfo}
                />
              ))} */}
              <div
                className="thread-replies-container"
                ref={threadScrollRef}        // attach ref here
                onScroll={handleThreadScroll} // attach scroll handler here
              >
              {/* {validReplies.map((reply, idx) => {
                // Current reply's date
                const currentDateObj = new Date(reply.sentAt * 1000);

                // Previous reply's date
                const prevReply = idx > 0 ? validReplies[idx - 1] : null;
                const prevDateObj = prevReply ? new Date(prevReply.sentAt * 1000) : null;

                // Compare only day (ignore time)
                const isNewDay =
                  !prevDateObj ||
                  currentDateObj.setHours(0, 0, 0, 0) !== prevDateObj.setHours(0, 0, 0, 0);

                // Format for display
                const formattedDate = formatChatDate(reply.sentAt);
                
                const currentDate = new Date(reply.sentAt * 1000).toDateString();
                const nextReply = validReplies[idx + 1];
                const isLastDateGroup =
                  !nextReply ||
                  new Date(nextReply.sentAt * 1000).toDateString() !== currentDate;

                return (
                  <React.Fragment key={reply.id}>
                    {isNewDay && <div className="cc-date-separator" 
                      style={{
                        position: isLastDateGroup ? "relative" : "sticky",
                        top: isLastDateGroup ? "auto" : "12px",
                        zIndex: 2,
                        display: "block",
                        width: "fit-content",
                        margin: "20px auto"
                      }}>
                    {formattedDate}</div>}
                    <MessageBubble
                      msg={reply}
                      loggedInUid={loggedInUid}
                      apiMessages={apiMessages}
                      onOpenMessageInfo={onOpenMessageInfo}
                    />
                  </React.Fragment>
                );
              })} */}
              {sortedReplies.map((reply, idx) => {
                  const currentDateObj = new Date(reply.sentAt * 1000);
                  const currentDateStr = currentDateObj.toDateString();

                  // Previous message date (for detecting new day)
                  const prevReply = idx > 0 ? sortedReplies[idx - 1] : null;
                  const prevDateStr = prevReply ? new Date(prevReply.sentAt * 1000).toDateString() : null;

                  // Is this message the first message of a new day?
                  const isNewDay = currentDateStr !== prevDateStr;

                  // Last message date (for sticky positioning)
                  const lastDateStr = new Date(sortedReplies[sortedReplies.length - 1].sentAt * 1000).toDateString();
                  const isLastDateGroup = currentDateStr === lastDateStr;

                  const formattedDate = formatChatDate(reply.sentAt);

                  return (
                    <React.Fragment key={reply.id}>
                      {isNewDay && (
                        <div
                          className="cc-date-separator"
                          style={{
                            position: isLastDateGroup ? "sticky" : "relative",
                            top: isLastDateGroup ? "12px" : "auto",
                            zIndex: 2,
                            display: "block",
                            width: "fit-content",
                            margin: "20px auto"
                          }}
                        >
                          {formattedDate}
                        </div>
                      )}
                      <MessageBubble
                        msg={reply}
                        loggedInUid={loggedInUid}
                        apiMessages={apiMessages}
                        onOpenMessageInfo={onOpenMessageInfo}
                      />
                    </React.Fragment>
                  );
                })}
              {/* Scroll-to-bottom button */}
              {showScrollDown && (
                <button
                  className={`cc-scroll-down-button ${showScrollDown ? "cc-scroll-down-button--visible" : ""
                    }`}
                  onClick={scrollToBottom}
                >
                  {/* You can replace this with your uploaded arrow image */}
                  <img
                    src="data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2221%22%20viewBox%3D%220%200%2020%2021%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M9.99841%2012.9961C9.90813%2012.9961%209.82536%2012.9805%209.75008%2012.9492C9.67494%2012.918%209.60376%2012.8665%209.53654%2012.7948L5.55049%208.80859C5.41619%208.67901%205.35195%208.52276%205.35779%208.33984C5.36362%208.15707%205.43362%208.002%205.56779%207.87464C5.70209%207.74727%205.85897%207.68359%206.03841%207.68359C6.21772%207.68359%206.37105%207.74727%206.49841%207.87464L9.99841%2011.3886L13.5124%207.87464C13.6396%207.74727%2013.7906%207.68595%2013.9655%207.69068C14.1402%207.69526%2014.2947%207.76123%2014.429%207.88859C14.5632%208.01582%2014.6309%208.17089%2014.6322%208.3538C14.6333%208.53672%2014.569%208.69297%2014.4395%208.82255L10.4672%2012.7948C10.3954%2012.8665%2010.3219%2012.918%2010.2467%2012.9492C10.1715%2012.9805%2010.0887%2012.9961%209.99841%2012.9961Z%22%20fill%3D%22%23A1A1A1%22%2F%3E%3C%2Fsvg%3E"
                    alt="Scroll down"
                    style={{ width: "24px", height: "24px" }}
                  />
                </button>
              )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface MessageContentProps {
  msg: ApiMessage;
  isOutgoing: boolean;
  loggedInUid: string;
  onOpenMessageInfo: (msg: ApiMessage) => void;
}

export const MessageContent: React.FC<MessageContentProps> = ({
  msg,
  isOutgoing,
  loggedInUid
}) => {
  if (msg.deletedAt) {
    return (
      <div className="cometchat-delete-bubble">
        <div>This message was deleted</div>
      </div>
    );
  }

  return (
    <>
      {msg.type === "text" && (
        <div className="cc-text">
          {renderTextWithMentions(
            msg.data?.text || "",
            msg.data?.mentions,
            loggedInUid,
            msg?.receiverType,
            "text"
          )}
        </div>
      )}

      {msg.type === "audio" && (
        <AudioMessage
          msg={msg}
          isOutgoing={isOutgoing}
          downloadFile={downloadFile}
        />
      )}

      {msg.type === "file" &&
        msg.data?.attachments?.map((file) => {
          // const ext = file.extension.toLowerCase();
          // const isImage = ["png", "jpg", "jpeg", "webp"].includes(ext);

          // if (isImage) {
          //   return (
          //     <div key={file.url} className="cc-image">
          //       <img src={file.url} alt={file.name} />
          //     </div>
          //   );
          // }

          // return (
          //   <div key={file.url} className="cc-file">
          //     <div className="cc-file-name">{file.name}</div>
          //   </div>
          // );
          const ext = file.extension.toLowerCase();

          const isImage = ["png", "jpg", "jpeg", "webp"].includes(ext);
          const isPDF = ext === "pdf";
          const isText = ext === "txt";
          const isXLSX = ext === "xlsx";
          const isDOCX = ext === "docx";
          const isPPTX = ext === "pptx";
          const isDocument = ["doc", "xls", "ppt"].includes(ext);

          const videoFormats = ["mp4", "mov", "avi", "mkv", "webm", "flv", "wmv", "m4v"];
          const audioFormats = ["mp3", "wav", "ogg", "flac", "aac", "m4a"];

          // Helper to truncate file names
          const truncateName = (name: any) => name.length > 15 ? name.slice(0, 15) + "..." : name;

          if (isImage) {
            return (
              <div key={file.url} className="cc-image">
                <img
                  src={file.url}
                  alt={file.name}
                />
              </div>
            );
          } else if (isPDF || isText || isXLSX || isDOCX || isPPTX || isDocument || videoFormats.includes(ext) || audioFormats.includes(ext)) {

            let iconSrc = "";
            if (isPDF) {
              iconSrc = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI/SURBVHgB1Ve/L2xBFP7m7L2bt7F5b98riO4hCgmFSkWEmmi1Qq3X06j8FXQWtR8lUWyIRiIhGiHBJn6sFXfGOWsb6y5zZ+9d8SWTmcyczPedM2fmngswjDH/uW1zuzXNwapwCreqDgrccmguitz6RcAqDyZqV83lJcz5OaKCenqAbNbWfEcEmA/k+/t4WVwEymVEBc3MIDU6CmQyNuZFCpsN8nkn8gqenqDZAZRKNtY5QhJgclsRyQiIICI5AZYiYhdgDg8RRYSHuAUcHOD6+Bjl9naklKp4KP2vkxNkp6aSFyDI8U24YRF3QQCPyT0ikO+H2jofgWppgRoYqLv+L53GXyYVchEhUQiDcwRofh7U2opgeRl6YyPU5rfnwdcaz9xiFyAR0Ht7APefIVONgKojwPkI9O4uSI7g6upLW1/yIG4Bpuq9OT21sld15t3fgYeHtw16e9EInAXQ2FjFe5qchOrogCvck7CvD8HCAtTICFJzczCcE1IHKL4ZenMTemvLah+nCBCTyhEoDr9qa4PhRCSuASQppZBJzc5a7xUpAnL1aHwcSsJ/dFQ5AsOeGhaj2XN5mFRnJ4KlJes9rQWIdzQ9/Xb/19cRrKy8W5comDoP0qf7WhtyWIVE7n0teSOwjoDmJ1cS7CVG8mgCHMJrg2Qrop8r4IsvXNwCirWT90NDKHE1Exdkr9vBwdA1+TPa5n64duHx4gLFQgFpavyU/K4u/OnuDlta+/afU+JK5UwG3PJoDoR4RziF+xXwDkgcKZ6uuwAAAABJRU5ErkJggg==";
            } else if (isText) {
              iconSrc = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAH5SURBVHgB3ZfLSxxBEMa/7s48FkIiG3LYJWxiSMwp5HHJNZBAcgrJHxrMHuJVbyJ7EEEEX+wDcdVdcPGgzmvHqlEPrqvMTPes4g+K7uluur6pLmp6ACKO41dk82SH8WSYZZ/sW1x0lsmmMFkGZJ9YwCx1fl+bHRzjoH+ErNRePIPrWmmXL7CAeHR0c3sPf+tLCMIIWfnx7QM+vn8J236UZvlAjhtdbGzkcs74foiNrW7SpmBKogCyiChEQBYRhQlIK8K4gFb7AFlEpErVLDQ7PbQ6u3j6xIEUEkLKpN3Z7eHn98/FC2BiWOjuH8H3TiClglQKyhrvqhABTKn0mN5cIQy9JAJSCGgLcB0LMzOVW9esb3bhnQZJ33FLkL7EcBhBCKkvgKlVy8mGzOvpCriMNptd+qoI/qphba1zZb1lO4iikKYNRODUC1Cfa2AYDZPnP7++0OYR6v8bEDh3kJy3urotP0tlKAeUssjO+5xgLMCy7BtDfMkNATBVBwTyYkRAfveGBMTIj6EjyC/hYeSATgS0SnGr3UvqvQ5aAlZW2wgCj+5/LvKidQRcXnWcawswwf0U4Do2JilgMDr47s1zSi4fpuC93k6Xx87xn9E8tV9HJ/r9Q7pcdKCkgi7VShm1WnXcVP3Of07pqiZa3CH7h8nAjhfYJ/s+A9xpLS5WrHVdAAAAAElFTkSuQmCC";
            } else if (isXLSX) {
              iconSrc = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMRSURBVHgBxVddSBRRFP5m9tfVQhLZqEzTJMkQwwQRIQt6kCiEtF4qeqkgo6B8KurBZ+mHIkoyzIcgsUzChDDcIMjC0MQty8RFDf9zU2TX/e3cu9vm/tU4u04fXGY4d+aeb875zrl3AILX682g0UljzqsMWphP5lvw3/TQSIaysNLYyQi00E156OzUzDxGx39EfTs1ZS2/Ts/OB9lzstYj0aCHRJgYAW+o9UO/BTfrX8LhcEV981bNUU7iyJnbEAQxYD9RWYLSohzo9VpIgFWMZG3r6P2rcwbL2Az6B0aQk2nE9uwNAbttyYlu+gC73QEJSFZDJmrvvYDb5UBz3QWehrNXGgORsNt9JHbtyPhnJGQT2L83HwaDDqa3n7CwsAivxw1B9SegUknIJlC2J49r4NDJaxDJsSiELyWFhGwCdx52wOm0oaa6Eja7C7V17dxuHviOooIsySRkE/g8NMGvudvSqGR/wuNxQRTV6P86hsHhERjXJUEQBXpC4NoYtozjWMXu+BG4er6cUrAGVZcewEVixLJiXnKK+DYyDZdziTsXRRU0msiuZBNITUniBCanrVBr9aSD4KW0ukTShQput5MiIQb1irgQqLrcwL/86f1qKsMFfxkKwYsTMcElUoV4wuZiJpCZboQhQQcz5Xxyas5fhuHLqdRa0oeb0hDnCFw8VcbLsOL0dbadUgo0UZ9lGhBoxJVA26te6HUicrM3kuh8rVkOZBNoN/Xx3DbdPQfzl1HU3GjlYlOMAFM1a71Nz7swMTVLWSANQEECv/GkvdsnwCg5XjUCxytKkJjga62LNgcam99ADmQTKMzLCJyKWCtueGwKa0arSoCh/lEHa/U4sK8QQb14BVi5apahu28YPeYxxALZEXjdNYDDB4uRZEjAu55B8FAoSaC57b1vFwRvhFCrJR1C40eA6g5qjeTjd1TEpIF4ICIBtsspScAaaiwuSOdH7niBrVWUnxZxjv0ZddK1NHRiYnIWH81DfCuNFVs2G7E1c1Okqdb//nMq0lHJwm5oPIMyYI5NzCfz/QsYRIzAPAyESAAAAABJRU5ErkJggg==";
            } else if (isDOCX) {
              iconSrc = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAK7SURBVHgBzZdPTxNBGMafmd0thQoWo0SJiQjqAQSjJkSjiZoQTgb5BHoyxC/B1ZN+C08kolcvEKMSPYgtMRCkFQ0BMREahZb+m/F9t00jbUlmsC08yWQ3M7Pz/OZ98+7OAiStdRe1KWqbujGaZE/2FsWbWWphNFYJapcZYJJuRstHf2xofFvXsFXvGYnWFuPp0wxQ4fJ+XuHxsxzSWVjr0T2JoasuWpqMpidktd7nr9W+zFnJHd5AHsm00fSwRB2USptD1AXABqJuAKYQNQf4FNNWEC5qrMiSxuLXX+hsJ0fhQAhJzcHS92Y8HDlSfwBWKh/GwsoG8pk/ENL1m+cKGmkQAMsLHvONVS5VgqimugH4iwfaoKQHpbJ+Gv4LIBQsXLd37Pqk2wyhyEaKqusaV8HoTQdjd3cvMv7Axfh92plWpb6xERdDV9SueYKisFcKjAHm4hrXLzrklSn1dZ8COtpB7/1cqW+gW+Djwm+6K4PYw8oYIBpXlEeBjrAuGklEvqQRXUzj7EldBBJ0tsgjvqp9SxNZvYhmPlMUevkRuvYJvI2mMLcscaOfw6vQ0yl8KIfyXhcAjsKlHtcH4FBHY3nMLAj093BqNK71SR9KOgHjNS0joDBw3sOJthyUzmF9U2A7JegA4iHUlCU4hqLcC7PdWwNspYD4WqEiYis5KrFACWxsJICllSySGQ+m4bcGYHE1DA824d0/oWaA4cEg3kSS1OdZrWf9JmSz461ZRGJceoWdxtY0Xn3Ywcxcmuo9aLWeNUCMSuzpRJ7SHCKzAsA2pebJBJ3hdJAiYBfUfX0LpFN54pTS7BRa8RwOWIcTINSMhgIkyjvv9G/RQSKJWonXutW3WXWM/4ym6Hq7fGD1ZxKz8wk61tnVdTWdOx3Aha6j1YZeHvjPqaRP7DLfUHuBxoiNp9mTvf8CwVZ4KCLQmwsAAAAASUVORK5CYII=";
            } else if (isPPTX) {
              iconSrc = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAJHSURBVHgB1ZdNaxNBGMf/M7uTxHRTqx5K0EIR1HoSv4Dk4KWI4DfwI3jy4En0s4j0Vl8uHtQUvVVMD0HFolLEBg+NTRNtsuvujs/TFLTZFLOzL+I/DNl52fn/ZubZ2VmApLWep1SntK3z0TJ7srfYv1ijNIN81aF0kQGW6eJapLq7CbQ/IbaqF4DS9KStVxhAR4o3XgJPbgH+ALF16SawcBUolCdp3ZFji9fumZmzvF0awIvh/981I5GFYkBkAxADIjuACSHSB/jyCnEgbKQOsIrtz2/hOnOQUsASNEohUGy9R7l2IwcA0jH5Hd+2mugOQtgEoYhCKjW2bSYArONlm8xD9H8SBAFYBJIeAG80f8rr0c7ZArbWDxRPlyQURZkXgpYiTYDLt+Gu1xG6vb2sLFZgz56D1SaAZ3eA/XLWkYKEHehDw914CdzGfVit1xD08ynvqSlUri/BP3sFdnPpQFuOAZ3qEvCNNKU8ut/d0mPWasC1HEge8IjfISuQdB8Y6bVSRdDvIY6MZ0CdqUGcODnMFB3gdA3BVBX+h3qEKxOAwCEz5QwzFHTBu+foNx+jIn5A2pMTGAN4FITqa4OOVMM8P24lC3sbTxwZA5AXSrZAPLuojINQCIHk9oYA/ptHCAZdaGgkldES6Kd3UQg1hEo+A0YAigPASm7OyvZE9P8C0NstT4DOaOHO/CJ2+SWekriv9tzi2Dr+MqLNG7XITe1N7HxcRcFCYtmzCzh66vy4qof//OOUDqxigy8oPUA+YuMV9mTvX6exKJpgmkTmAAAAAElFTkSuQmCC";
            } else if (isDocument) {
              iconSrc = ""; // you can use emoji 📄
            } else if (videoFormats.includes(ext)) {
              iconSrc = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI/SURBVHgB1ZfPaxNBFMe/O/tjdtc2JoKgBSXoqXrSk0ftxUNB1KP4H/iHqada0WtSClIVtDdpoYTQQtWqSbYlyf5Isr7ZiIcwxtnsbkq/8Nhlfuz77Hszb2cBUhzHVbI6WTuej9aET+Fb+3OzTVbGfNUhuyUA1ujm4WRv2xvh6NcQaXV1SYdjM9XhGwIgnmzdbUR4+baLaBAjrVbvubh9w4JlaSrDO1LUd5/8mZwLBWGMHXqBMFSaX1aOVVEQhQCkgSgMQBUid4DGfoQ0EAbyBjgYoLnfQnkxgqbp0BgDo+vBoYvVlQvFAwiNtBIOj9oIgmMwZiRmGPJtmToFly/qKJf+P812K7CdyhhANygS8ndNHYFH988lELX3Pupb/aljub0InZwPhxGlgeUD4PBxKFfu2EnFqxHE9pfwn+MN00mioDFd2p9pF4hUPKaIPHmwMDUtTDcTCCkgctDydZPsfJKWrc8+/ECy5bScFuE0ibQ8e1pK1oiqci9E3AJc3oPkIytVrnVgr9nDi9ffcNIzYAmSeQG0OhGer3+lKhgmW8/iJrUqnQeyAfT9ETY/tLD50cModuC4C7TW1BxnBhiH+zuOuwymWaJSO9ujUs96UzuB53l/w81t9XDnArDbGFJp5XAp3NBmdzwzgHCqG2orXEWFnojOLgDn2XObBqAz2XjzWoBB1EdeiuhZy9WutE/8GdXpeney48fPLnb22smBIquWLnFUr1RkXeun/nPKqHQ2xQ3ZK8xHwvGG8Cl8/waJH0DPGwrofQAAAABJRU5ErkJggg==";
            } else if (audioFormats.includes(ext)) {
              iconSrc = ext === "ogg"
                ? "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAH6SURBVHgBxVfbasJAEJ3EeL8QURG8QB/EF5/6B/2E/mnbL9B3hf6B9s07jTdUUNM9SxMkTSCb3dgDQ5bdDedkZnZmQ8Rg2/YTsz6zb/sxeAMnuLXfwSczkx4Li9kzBLyxwat39Xw+0/F4JFEUi0VKJBJhtw8gwPbOWpZFk8mEbrcbiaLdblO1WiVd18Nst3x3LRaLSOTA9XrlHxDyfTOUzDhFxCJARERsAsKKUC5gt9uRiAiDYhAwn8/5Kbi3crlMnU4nfgFALpej7XZLh8OB1wTH/BCLAKBUKnFSFDQ8g+pCZAGmaXID4Pb1ev1nTz6f5+SXyyXQA5GSsNVqUbPZpOVyya1er1O32yWfokqZTIabshAgviAcDod0Op0omUzyTO/1enzNr38YhqFOAL5yOp3yBEPjATRNc9eC4OyRFoAv3Gw2lM1m3blGo8Hn0UMgKohMiQAglUq5Y+RDOp2m0WjEQyBCDkhVwkql4uYD4gwThZSAQqHA3Q7ce0UEUoVotVrRfr/nIYgKKQ8g3vBCFNc7kPIAcgA1AJUw5BVMrYDxeMxDAC9EhZQAVDc0HdGjdw/pC4kMuRIBsvAVIPBjoUSA5Z1E+8RFQhXQNYOOKv6M+uz54l3AlWo2m0mdcQdoULVazW/p499/TnWWxV8YMHunxwDEA3CC+wfREERCXNxfDQAAAABJRU5ErkJggg=="
                : "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAI4SURBVHgBxVfPaxNBFP5msttkA8GkuYlo7MGbsj0JXvTsxSgeBIV48ij1L2h79CKIRw/BP0BTUeypZm96M6g9qKBREESsDUTT/NhkfLO20GQ3MNOdTT947GTeZL9v33vzdhYgCCFKZHWybTEb1CSn5Ga7gzdkecwWLbJFKaBGg/Kkd/TrC0Y/NqELvnAOPDuvutyTAsTkrP/+GTrVa0C/A11krj6AfbYClsmpLG/xqNn+y3sHIpcQvTY9wHOIbltleZ4jAeiISESAjojEBKiKMC5g+GEDOiIsGIb/cQNbnxsYFE+BM4YUQ2DOtwYKV+6aF2CfLmPwbm1s7oi/he2vr9AeEAFnZBRqay7y/7FSYJ0pw7lVC66TKGTI0gjILS7ApzDFEsCdPEa/m3BuVKn53Az5c/TQOVvAohRIMy5AYvD6EToPLyN9cRnZ23Wkjrljfsf6b4lEYA/DTx7+LJ9Ef30VmevVUEpsYrGTisB++CSk++QOCbgU8k3hN78NZTSYo/5mT6QT+m/XlNcm2opVoJUCls0jddQFl5XeoQNN8QT1WsSCsgBeLAXbjM+XxuZ7L1YRB8opmDu/FCI3AWUBYqcVOT/83sBMBPTWV9D37mO/IPlbp+KjoFWE3cdLlPOVoBgFFeG0qCQmQEKSmiDew6H3gWgBGq3UhIBQPLuLFez4MAZ5r7ZbifTJL6M6XS9MOv7+bKK16QXnubhIH3dRWHCjXE8P/eOUDq6sKQdk8Ta0HrEnOSX3PxBZPjDxF3YdAAAAAElFTkSuQmCC";
            }

            return (
              <div key={file.url} className="cc-file">
                {iconSrc ? (
                  <img src={iconSrc} alt={ext.toUpperCase()} className="cometchat-file-bubble__leading-view" />
                ) : (
                  <div className="cc-file-thumb">📄</div>
                )}
                <div className="cc-file-info">
                  <div className="cc-file-name" onClick={() => window.open(file.url, "_blank")}>
                    {truncateName(file.name)}
                  </div>
                  <div className="cc-file-meta">
                    {/* {Math.round(file.size / 1024)} KB • {ext.toLowerCase()} */}
                    {formatSize(file.size)} • {file.extension.toLowerCase()}
                  </div>
                </div>
                <DownloadIcon
                  size={20}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    downloadFile(file.url, file.name);
                  }}
                  color={!isOutgoing ? "#e09029" : "#ffffff"}
                />
              </div>
            );
          } else {
            // Default fallback for unknown file types
            return (
              <div key={file.url} className="cc-file">
                <div className="cc-file-thumb">📁</div>
                <div className="cc-file-info">
                  <div className="cc-file-name">{file.name}</div>
                  <div className="cc-file-meta">
                    {Math.round(file.size / 1024)} KB • {ext.toUpperCase()}
                  </div>
                </div>
                <DownloadIcon
                  size={20}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    downloadFile(file.url, file.name);
                  }}
                  color={!isOutgoing ? "#e09029" : "#ffffff"}
                />
              </div>
            );
          }
        })}
    </>
  );
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  msg,
  loggedInUid,
  apiMessages,
  onOpenMessageInfo
}) => {
  const [threadHoveredMessageId, setThreadHoveredMessageId] = useState<string | null>(null);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const senderId =
    typeof msg.sender === 'object' && msg.sender !== null
      ? msg?.data?.entities?.sender?.entity?.uid
      : msg.sender;

  const isOutgoing = senderId === loggedInUid;

  function getReceiptClass(msg: ApiMessage) {
    if (msg.readAt) return "cometchat-message-bubble__status-info-view-receipts-read";
    if (msg.deliveredAt) return "cometchat-message-bubble__status-info-view-receipts-delivered";
    return "cometchat-message-bubble__status-info-view-receipts-sent";
  }

  const formatChatDate = (unixSeconds: number) => {
    const msgDate = new Date(unixSeconds * 1000);
    const today = new Date();

    const isToday =
      msgDate.getDate() === today.getDate() &&
      msgDate.getMonth() === today.getMonth() &&
      msgDate.getFullYear() === today.getFullYear();

    if (isToday) return "Today";

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isYesterday =
      msgDate.getDate() === yesterday.getDate() &&
      msgDate.getMonth() === yesterday.getMonth() &&
      msgDate.getFullYear() === yesterday.getFullYear();

    if (isYesterday) return "Yesterday";

    const day = String(msgDate.getDate()).padStart(2, "0");
    const month = msgDate.toLocaleString("en-US", { month: "short" });
    const year = msgDate.getFullYear();

    return `${day} ${month}, ${year}`;
  };

  const date = formatChatDate(msg.sentAt);

  const index = apiMessages.findIndex((m: any) => m.id === msg.id);

  const prevDate =
    index > 0 && apiMessages[index - 1]
      ? formatChatDate(apiMessages[index - 1].sentAt)
      : null;

  return (
    <>
      {msg.parentId === undefined && date !== prevDate ? <div className="cc-date-separator">{date}</div> : msg.replyCount && date === prevDate && <div className="cc-date-separator">{date}</div>}
      <div
        className={`bubble-container ${isOutgoing ? "bubble-container-out" : "bubble-container-in"
          } ${threadHoveredMessageId === msg.id ? "out-hightlight-message" : ""
          }`}
        onMouseEnter={() => { isOutgoing && setThreadHoveredMessageId(msg.id); }}
        // Optional: Clear the highlight when the mouse leaves
        onMouseLeave={() => { isOutgoing && setThreadHoveredMessageId(null); setHoveredMessageId(null); }}
      >
        {/* This button stays hidden (opacity-0) UNLESS the parent 'group' is hovered */}
        {threadHoveredMessageId === msg.id &&
          <button
            className="menu-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              setHoveredMessageId(msg.id);
            }}
          >
            <MoreVertical size={16} color="#A1A1A1" />
          </button>
        }
        {msg.parentId && msg?.receiverType === "group" && !isOutgoing && (
          <div className="cc-avatar-wrapper">
            <Avatar name={msg?.data?.entities?.sender?.entity?.name} />
          </div>
        )}
        <div className="cc-message">{msg.parentId && msg?.receiverType === "group" && !isOutgoing &&
          <div className="cc-sender-name">{msg?.data?.entities?.sender?.entity?.name}</div>
          }
          <div className={`bubble ${isOutgoing ? "bubble-container-out out" : "bubble-container-in in"}`}>
            <MessageContent msg={msg} isOutgoing={isOutgoing} loggedInUid={loggedInUid} onOpenMessageInfo={onOpenMessageInfo} />
            <div className="cc-time">
              {new Date(msg.sentAt * 1000).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
              {isOutgoing && (
                <span
                  className={`cometchat-message-bubble__status-info ${getReceiptClass(
                    msg
                  )}`}
                >
                  <span className="cometchat-message-list__receipt" />
                </span>
              )}
            </div>
            {isOutgoing && hoveredMessageId === msg.id && (
              <div
                className="cc-info-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenMessageInfo(msg);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#FFFFFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12" y2="8" />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
